// Reconciliation view for the Billing tab.
//
// Every field ticket that goes out records the HeavyJob values it was built
// from in PC.Field_Ticket_Reconciliation (billingRecon.js has the why). This
// view reads a week of them back and runs two checks on each row:
//
//   HeavyJob Check — the HeavyJob hours cached when the ticket went out
//                    against the cards as they are now. A card edited after
//                    the client signed shows here as Changed / Removed / New
//                    before JDE payroll carries it onto an invoice.
//   Invoice Check  — the hours the ticket printed against the JDE cost
//                    history lines the invoice bills (the Transactions
//                    view's lines). Scoped to one summary (the Invoices
//                    view) only this check shows.
//
// Every row carries the whole Labor & Equipment Billing Key (New Fusion Billing
// Table.xlsx) in its column order: HeavyJob card → field ticket → JDE line,
// then the daily ticket, weekly ticket and invoice statuses with their first
// three signers, read live off the DocuSign envelopes and the billing
// summaries (billingRecon.statusesOf). Record to table writes it all back
// onto the stored rows; Export to Excel writes every column of the sheet.
// The grid shows a trimmed read of it (screenColumns).
//
// Any span of work dates reads: a week ending, a preset (this week, last
// month…) or From / To.

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import moment from 'moment';
import XLSX from 'xlsx';
import { View, Text, Pressable } from '../lib/react-native-compat';
import {
    fieldTicketReconSnapshots, fieldTicketReconRead, fieldTicketReconRecord, loadHeavyJobCards,
    fieldTicketReconReviews, fieldTicketReconReview, fieldTicketReconUnreview,
} from '../db/company';
import { showToast } from '../helpers/toast';
import { weekEnding } from './billingTicketDocs';
import { rulesFor, foldRows, applyPeople } from './billingCardSource';
import { tierDescr } from './billingEquipMap';
import usePersistedState from './usePersistedState';
import { draftsOver, draftStatus, lastReview } from './billingInvoiceDrafts';
import { invoicedWeeks } from './billingInvoiceWorkbook';
import {
    fromDb, currentRows, compareHeavyJob, withJdeOnly, matchJde, recordPayload, snapshotWeeks, statusContext, statusesOf, groupRows, invoiceSplit,
    jdeClaims, jdeKeyOf, HJ_STATUS, JDE_STATUS, STATUS, RECON_TOL, r2, normCode,
    reviewKey, reviewMap, applyReviews, reviewPayload, REVIEWED, REVIEW_STALE,
} from './billingRecon';

const card = {
    backgroundColor: '#ffffff', borderRadius: 10, borderWidth: 1, borderColor: '#e2e8f0',
    paddingTop: 16, paddingBottom: 16, paddingLeft: 20, paddingRight: 20, marginBottom: 16,
};
const muted = { fontSize: 11, color: '#64748b' };
const md = (d) => (d ? moment(d, 'YYYY-MM-DD').format('M/D') : '');
const num = (v) => (Number.isFinite(Number(v)) ? Number(v) : 0);
const fmtDate = (d) => (d ? moment(d, 'YYYY-MM-DD').format('ddd M/D') : '—');
// Captured_At_Utc is genuine UTC off SYSUTCDATETIME().
const fmtUtc = (t) => (t ? moment.utc(t).local().format('M/D/YY h:mm a') : '');
const fmtH = (n) => (n == null ? '—' : Number(n).toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 2 }));
const fmtDelta = (n) => (n == null || Math.abs(n) <= RECON_TOL ? '' : `${n > 0 ? '+' : ''}${fmtH(n)}`);
const fmtMoney = (n) => `${num(n) < 0 ? '-$' : '$'}${Math.abs(num(n)).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
// Whole dollars — the amount column when the grid has squeezed it below
// what cents need (a week's $976,055.80 clipped to "$976,055." otherwise).
const fmtMoney0 = (n) => `${num(n) < 0 ? '-$' : '$'}${Math.round(Math.abs(num(n))).toLocaleString('en-US')}`;
const shortKey = (s) => {
    const list = String(s || '').split(',').filter(Boolean);
    if (!list.length) return '';
    const first = list[0].length > 12 ? `${list[0].slice(0, 8)}…` : list[0];
    return list.length > 1 ? `${first} +${list.length - 1}` : first;
};
const PART_LABEL = { day: 'Day crew', night: 'Night crew', summary: 'Week summary', all: 'Whole ticket' };

const TONE = {
    good: { bg: '#dcfce7', fg: '#166534' },
    bad: { bg: '#fee2e2', fg: '#991b1b' },
    warn: { bg: '#fef3c7', fg: '#92400e' },
    quiet: { bg: '#f1f5f9', fg: '#475569' },
    // A check that passed: plain grey text, so the ones that didn't stand out.
    ok: { bg: 'transparent', fg: '#94a3b8' },
};
const HJ_TONE = {
    [HJ_STATUS.same]: TONE.ok,
    [HJ_STATUS.changed]: TONE.bad,
    [HJ_STATUS.removed]: TONE.bad,
    [HJ_STATUS.added]: TONE.warn,
    [HJ_STATUS.fresh]: TONE.warn,
    [HJ_STATUS.jdeOnly]: TONE.warn,
    [REVIEWED]: TONE.good,
    [REVIEW_STALE]: TONE.warn,
};
const JDE_TONE = {
    [JDE_STATUS.match]: TONE.ok,
    [JDE_STATUS.differs]: TONE.bad,
    [JDE_STATUS.code]: TONE.warn,
    [JDE_STATUS.orphan]: TONE.warn,
    [JDE_STATUS.pending]: TONE.quiet,
    [JDE_STATUS.perDiem]: TONE.warn,
    [REVIEWED]: TONE.good,
    [REVIEW_STALE]: TONE.warn,
};
const Pill = ({ label, tone }) => (label ? (
    <span style={{
        display: 'inline-block', fontSize: 10, fontWeight: 700, whiteSpace: 'nowrap', borderRadius: 999,
        padding: '1px 8px', backgroundColor: (tone || TONE.quiet).bg, color: (tone || TONE.quiet).fg,
    }}
    >
        {label}
    </span>
) : null);

// ── Reviews ──
// A row somebody looked at and wrote why it differs (`rv`, applyReviews).
// Its check reads Reviewed and is no exception — until that check moves
// from what was reviewed, when it reads "Changed since review" and flags
// again. `side` is the check: 'ticket' (HeavyJob) or 'invoice' (JDE).
const reviewStatus = (r, side) => (r.rv ? ((side === 'ticket' ? r.rv.staleTicket : r.rv.staleInvoice) ? REVIEW_STALE : REVIEWED) : '');
const reviewedOk = (r, side) => reviewStatus(r, side) === REVIEWED;
const reviewTip = (rv) => [rv.note, [rv.by, rv.at ? fmtUtc(rv.at) : ''].filter(Boolean).join(' · ')].filter(Boolean).join('\n');
// The check cell of a reviewed row: the pill, the note, and ✕ to reopen
// (`brief`: the pill alone, when another check cell carries the rest).
const ReviewCell = ({ r, side, tone, onReopen, brief }) => {
    const st = reviewStatus(r, side);
    if (brief) return <span title={reviewTip(r.rv)} style={{ cursor: 'help' }}><Pill label={st} tone={tone[st]} /></span>;
    return (
        <span title={reviewTip(r.rv)} style={{ cursor: 'help' }}>
            <Pill label={st} tone={tone[st]} />
            {r.rv.note ? <span style={{ color: '#64748b', marginLeft: 4 }}>{r.rv.note}</span> : null}
            {onReopen ? (
                <span
                    role="button"
                    aria-label="Reopen"
                    title="Reopen — drop the review so the row flags again"
                    onClick={(e) => { e.stopPropagation(); onReopen(r); }}
                    style={{ color: '#94a3b8', fontWeight: 700, marginLeft: 6, cursor: 'pointer' }}
                >
                    ✕
                </span>
            ) : null}
        </span>
    );
};

// ── Discrepancies ──
// The cells that bill read against the field ticket, the hours the client
// signed: JDE and the invoice off it, JDE per diem off the HeavyJob adder.
// HJ Hours (now) lights when the card was edited after the ticket went out.
// HeavyJob at send off the ticket stays plain: that is the reviewed card at
// work, and Ticket vs HJ already shows it. A row carries its worst as a bar
// down its left edge (with its check's own tone); a week / day / person row
// tints each column a row under it has off. `tol` is RECON_VARIANCE: hours
// off by that much or less don't light (per diem, a count, stays exact).
const VS_TICKET = [{ key: 'jde', what: 'JDE' }, { key: 'invHrs', what: 'Invoiced' }];
const RECON_VARIANCE = 2;
const offCells = (r, tol = RECON_TOL) => {
    const out = {};
    if (!r.id) return out;
    const ft = r2(r.ft);
    VS_TICKET.forEach(({ key, what }) => {
        if (r[key] == null) return;
        const d = r2(num(r[key]) - ft);
        if (Math.abs(d) > tol) out[key] = { tone: 'bad', tip: `${what} ${fmtDelta(d)} vs the field ticket's ${fmtH(ft)}` };
    });
    // compareHeavyJob's status holds the variance (and a row added or zeroed
    // out in HeavyJob never reads Unchanged).
    if (r.hjNow != null && r.status && r.status !== HJ_STATUS.same) {
        out.hjNow = { tone: 'bad', tip: `HeavyJob ${fmtDelta(r.delta)} since the ticket went out (${fmtH(r.hj)} then)` };
    }
    if (r.k === 'Labor' && r.jdePd != null && Math.abs(num(r.jdePd) - num(r.hjPd)) > RECON_TOL) {
        out.jdePd = { tone: 'bad', tip: `JDE per diem ${fmtH(r.jdePd)} vs HeavyJob's ${fmtH(num(r.hjPd))}` };
    }
    return out;
};
const worst = (tones) => (tones.includes('bad') ? 'bad' : tones.includes('warn') ? 'warn' : '');
const BAR = { bad: '#dc2626', warn: '#f59e0b' };
const offStyle = (tone) => (tone ? { backgroundColor: TONE[tone].bg, color: TONE[tone].fg, fontWeight: 700 } : {});
const barStyle = (tone) => (tone ? { boxShadow: `inset 3px 0 0 ${BAR[tone]}` } : {});

// Stat tile: label over value, one cell of the .rc-kpis strip.
const Stat = ({ label, value, sub, tone }) => (
    <div className="rc-kpi">
        <div className="rc-kpi-l">{label}</div>
        <div className="rc-kpi-v" style={tone ? { color: tone } : undefined}>
            {value}
            {sub ? <span className="rc-kpi-s">{sub}</span> : null}
        </div>
    </div>
);

// ── Columns ──
// The Labor & Equipment Billing Key (New Fusion Billing Table.xlsx), in its
// column order, then each tab's own check columns. `get` feeds both the grid
// cell and the Excel export; `cell` (optional) renders something richer on
// screen; `group` is the band over the header (the sheet's Daily / Weekly /
// Invoice), `note` the sheet's line above a header, shown on hover.
// A week / day / person row totals its rows: `sum` columns add up (`sumCell`
// draws the total, `sumBlank` stands in when no row has a value), `agg`
// columns draw their own, and the rest show the value every row shares, or
// `many` of the distinct ones when they differ.
const who = (r) => (r.k === 'Equipment' ? r.unit : r.emp);
const plural = (n, one, many) => `${n} ${n === 1 ? one : many}`;
const tally = (vals) => Object.entries(vals.reduce((a, v) => ({ ...a, [v]: (a[v] || 0) + 1 }), {}))
    .map(([v, n]) => `${v || '(blank)'} ${n}`).join(' · ');
const unionKeys = (rows, keysOf) => [...new Set(rows.flatMap((r) => keysOf(r)))].join(',');
// A rental shows as its PO line (PO-231557-1 -> 231557-1), the way the
// equipment list names it.
const equipId = (r) => (r.k === 'Equipment' ? String(r.unit || '').replace(/^PO-/i, '') : '');
const fmtSigDate = (t) => (t ? moment.utc(t).local().format('M/D/YY') : '');
// `blankTip`: why a row's dash is a dash, on hover.
const hrsCol = (key, label, w, pick, note, blankTip) => ({
    key, label, w, right: true, note,
    get: (r) => (pick(r) == null ? '' : pick(r)),
    cell: (r) => {
        if (pick(r) != null) return fmtH(pick(r));
        const tip = blankTip ? blankTip(r) : '';
        return tip ? <span title={tip} style={{ cursor: 'help' }}>—</span> : '—';
    },
    sum: pick,
});
// A rental never has JDE hours to match: its cost posts as the rental PO's
// vendor voucher (52200 / 56100 / 56500, one per billing cycle), not by the
// day — so invoice hours, read off the matched JDE lines, stay blank too.
const rentalTip = (what) => (r) => (r.k === 'Equipment' && /^PO-/i.test(String(r.unit || ''))
    ? `Rental: JDE carries it as the rental PO's vendor voucher, not daily hours, so there are no ${what} to show.`
    : '');
const deltaCol = (col, pick, color) => ({
    ...col, right: true, sum: pick, sumBlank: '',
    sumCell: (t) => <span style={{ color, fontWeight: 700 }}>{fmtDelta(t)}</span>,
});
// A check column over a group: the status when every row shares it, else
// how many rows want a look (hover for the breakdown).
const checkAgg = (statusOf, tones, ok) => (rows) => {
    const all = rows.map(statusOf);
    if (new Set(all).size === 1) return <Pill label={all[0]} tone={tones[all[0]]} />;
    const off = all.filter((s) => s && s !== ok && s !== REVIEWED);
    const loud = off.filter((s) => tones[s] === TONE.bad || tones[s] === TONE.warn);
    if (!off.length) return '';
    return (
        <span title={tally(all)}>
            {loud.length
                ? <Pill label={`${loud.length} to check`} tone={loud.some((s) => tones[s] === TONE.bad) ? TONE.bad : TONE.warn} />
                : <Pill label={`${off.length} pending`} tone={TONE.quiet} />}
        </span>
    );
};
const STATUS_TONE = {
    [STATUS.pending]: TONE.quiet,
    [STATUS.draft]: TONE.quiet,
    [STATUS.sent]: { bg: '#dbeafe', fg: '#1e40af' },
    [STATUS.routing]: TONE.warn,
    [STATUS.rejected]: TONE.bad,
    [STATUS.signed]: TONE.good,
};
// Pending — nothing started yet — reads as a plain dash on screen, so the
// levels under way stand out; the export keeps the key's word.
const StatusPill = ({ status }) => (status === STATUS.pending
    ? <span style={{ color: '#94a3b8' }} title={STATUS.pending}>—</span>
    : <Pill label={status} tone={STATUS_TONE[status]} />);
// One level's status and its first three signers (email, date).
const statusGroup = (level, group, label, prefix) => [
    {
        key: `${level}Status`, label, w: 150, group,
        get: (r) => r.st?.[level] || '',
        // A send still out names who it waits on, so "Sent" says where it sits.
        cell: (r) => {
            const wait = r.st?.waiting?.[level] || [];
            const waitTip = wait.length ? `Waiting on ${wait.join(', ')}` : '';
            const waitTag = wait.length ? <span style={{ color: '#1e40af', marginLeft: 4 }}>{`→ ${wait.map((n) => n.split(' ')[0]).join(', ')}`}</span> : null;
            return level === 'invoice' && (r.invs || []).length ? (
                <span title={[`Billing summary ${r.invs.join(', ')}`, waitTip].filter(Boolean).join('\n')}>
                    <StatusPill status={r.st?.invoice || STATUS.pending} />
                    <span style={{ color: '#64748b', marginLeft: 4 }}>{r.invs[0]}</span>
                    {waitTag}
                </span>
            ) : <span title={waitTip}><StatusPill status={r.st?.[level] || ''} />{waitTag}</span>;
        },
        many: (vals, rows) => <span title={tally(rows.map((r) => r.st?.[level] || ''))} style={{ color: '#94a3b8' }}>Mixed</span>,
    },
    ...['1st', '2nd', '3rd'].flatMap((n, i) => [
        {
            key: `${level}${i}e`, label: `${prefix} ${n} Signature Email`, w: 150, group,
            get: (r) => r.st?.sigs?.[level]?.[i]?.email || '',
            cell: (r) => <span title={r.st?.sigs?.[level]?.[i]?.email || ''}>{r.st?.sigs?.[level]?.[i]?.email || ''}</span>,
        },
        {
            key: `${level}${i}t`, label: `${prefix} ${n} Signature Date`, w: 76, group,
            get: (r) => fmtSigDate(r.st?.sigs?.[level]?.[i]?.at),
            // On screen a signer who has not signed reads "not yet"; the export keeps the date blank.
            cell: (r) => {
                const x = r.st?.sigs?.[level]?.[i];
                return x?.at ? fmtSigDate(x.at) : x?.email ? <span style={{ color: '#94a3b8' }}>not yet</span> : '';
            },
        },
    ]),
];
const KEY_COLUMNS = [
    { key: 'id', label: 'Key', w: 56, frozen: 0, get: (r) => r.id || '', cell: (r) => (r.id ? r.id : <span style={{ color: '#94a3b8' }}>—</span>) },
    { key: 'type', label: 'Type', w: 76, frozen: 56, get: (r) => r.k || '' },
    { key: 'job', label: 'Job Number', w: 76, get: (r) => r.job || '' },
    {
        key: 'cc', label: 'Cost Code', w: 70, get: (r) => r.cc, cell: (r) => <span title={r.ccd || ''}>{r.cc}</span>,
        many: (vals) => <span title={vals.join('\n')}>{plural(vals.length, 'code', 'codes')}</span>,
    },
    {
        key: 'card', label: 'Heavy Job Time Card Key', w: 104, get: (r) => r.card || r.cardsNow || '',
        cell: (r) => <span title={String(r.card || r.cardsNow || '').split(',').join('\n')}>{shortKey(r.card || r.cardsNow)}</span>,
        agg: (rows) => {
            const keys = unionKeys(rows, (r) => String(r.card || r.cardsNow || '').split(',').filter(Boolean));
            return <span title={keys.split(',').join('\n')}>{shortKey(keys)}</span>;
        },
    },
    {
        key: 'jdeKey', label: 'JDE Job Cost History Key', w: 120, get: (r) => (r.jdeKeys || []).join(','),
        cell: (r) => <span title={(r.jdeKeys || []).join('\n')}>{shortKey((r.jdeKeys || []).join(','))}</span>,
        agg: (rows) => {
            const keys = unionKeys(rows, (r) => r.jdeKeys || []);
            return <span title={keys.split(',').join('\n')}>{shortKey(keys)}</span>;
        },
    },
    // The sheet has no shift column; the crew rides the date on screen.
    {
        key: 'd', label: 'Date Worked', w: 92, get: (r) => r.d,
        cell: (r) => (
            <span>
                {fmtDate(r.d)}
                {r.sh === 'Night' ? <span style={{ color: '#64748b', marginLeft: 4 }} title="Night crew">N</span> : null}
            </span>
        ),
        many: (vals) => `${md(vals[0])}–${md(vals[vals.length - 1])}`,
    },
    {
        key: 'eqId', label: 'Equipment ID', w: 96, get: equipId, cell: (r) => <span title={r.unitDesc || ''}>{equipId(r)}</span>,
        many: (vals) => (vals.length ? plural(vals.length, 'unit', 'units') : ''),
    },
    { key: 'eqTier', label: 'Strike Equipment Tier', w: 130, get: (r) => (r.k === 'Equipment' ? r.eqTier || '' : ''), cell: (r) => <span title={r.eqTier || ''}>{r.k === 'Equipment' ? r.eqTier : ''}</span> },
    { key: 'eqBill', label: 'Equipment Billing Tier', w: 130, get: (r) => (r.k === 'Equipment' ? r.eqBill || '' : ''), cell: (r) => <span title={r.eqBill || ''}>{r.k === 'Equipment' ? r.eqBill : ''}</span> },
    { key: 'emp', label: 'Emp ID', w: 76, get: (r) => (r.k === 'Labor' ? r.emp : '') },
    {
        key: 'name', label: 'Emp Name', w: 160, get: (r) => (r.k === 'Labor' ? r.name : ''), cell: (r) => <span title={r.name}>{r.k === 'Labor' ? r.name : ''}</span>,
        many: (vals) => (vals.length ? plural(vals.length, 'person', 'people') : ''),
    },
    { key: 'title', label: 'Strike Employee Title', w: 140, get: (r) => r.title || '', cell: (r) => <span title={r.title}>{r.title}</span> },
    { key: 'cls', label: 'Employee Billing Classification', w: 140, get: (r) => r.cls || '', cell: (r) => <span title={r.cls}>{r.cls}</span> },
    hrsCol('hj', 'HJ Hours', 60, (r) => (r.id ? r.hj : null), 'Pulled from the HeavyJob API when the ticket went out'),
    hrsCol('ft', 'Field Ticket Hours', 70, (r) => (r.id ? r.ft : null), 'What is stamped on the field ticket PDF'),
    hrsCol('jdeSt', 'JDE St Hours', 60, (r) => (r.jde == null ? null : r.jdeSt), 'Payroll hours from job cost history'),
    hrsCol('jdeOt', 'JDE OT Hours', 60, (r) => (r.jde == null ? null : r.jdeOt), 'Payroll hours from job cost history'),
    hrsCol('jde', 'JDE Total Hours', 64, (r) => (r.jde == null ? null : r.jde), 'Total payroll hours from job cost history', rentalTip('JDE hours')),
    // Per diem days: shown on the one row of each person-day that carries it.
    {
        key: 'hjPd', label: 'Heavy Job Per Diem', w: 70, right: true, note: 'The per diem adder on the HeavyJob card',
        get: (r) => (r.k === 'Labor' && num(r.hjPd) ? r.hjPd : ''), cell: (r) => (r.k === 'Labor' && num(r.hjPd) ? fmtH(r.hjPd) : ''),
        sum: (r) => (r.k === 'Labor' && num(r.hjPd) ? num(r.hjPd) : null), sumBlank: '',
    },
    {
        key: 'jdePd', label: 'JDE Per Diem', w: 64, right: true, note: 'Per diem (51500) days from job cost history',
        get: (r) => (r.jdePd == null ? '' : r.jdePd), cell: (r) => (r.jdePd == null ? '' : fmtH(r.jdePd)),
        sum: (r) => r.jdePd, sumBlank: '',
    },
    ...statusGroup('daily', 'Daily', 'Daily Field Ticket Status', 'FT'),
    ...statusGroup('weekly', 'Weekly', 'Weekly Field Ticket Status', 'FT'),
    ...statusGroup('invoice', 'Invoice', 'Invoice Status', 'IN'),
    // After the sheet's own columns in the export; on screen it follows JDE
    // Total Hours (the statuses move up, per diem moves after it).
    hrsCol('invHrs', 'Invoice Hours', 64, (r) => r.invHrs, 'Hours the billing summaries billed for the JDE lines this row matched', rentalTip('invoice hours (they come off the JDE lines)')),
    hrsCol('invSt', 'Invoice St Hours', 60, (r) => (r.invHrs == null ? null : r.invSt), 'Straight time the billing summaries billed (51100, and fleet usage)'),
    hrsCol('invOt', 'Invoice OT Hours', 60, (r) => (r.invHrs == null ? null : r.invOt), 'Overtime the billing summaries billed (51200)'),
];

const FT_CHECK = [
    hrsCol('hjNow', 'HJ Hours (now)', 70, (r) => r.hjNow),
    deltaCol({ key: 'delta', label: 'Change', w: 56, group: 'Check', get: (r) => r.delta || 0, cell: (r) => <span style={{ color: '#991b1b', fontWeight: 700 }}>{fmtDelta(r.delta)}</span> }, (r) => r.delta, '#991b1b'),
    deltaCol({
        key: 'gap', label: 'Ticket vs HJ', w: 64, group: 'Check', get: (r) => (r.ticketGap ? r2(r.ft - r.hj) : ''),
        cell: (r) => (r.ticketGap ? <span title="The ticket printed different hours than HeavyJob held when it went out — usually the reviewed time card." style={{ color: '#92400e' }}>{fmtDelta(r2(r.ft - r.hj))}</span> : ''),
    }, (r) => (r.ticketGap ? r2(r.ft - r.hj) : null), '#92400e'),
    {
        key: 'status', label: 'HeavyJob Check', w: 136, group: 'Check', get: (r) => r.status, cell: (r) => <Pill label={r.status} tone={HJ_TONE[r.status]} />,
        agg: checkAgg((r) => r.status, HJ_TONE, HJ_STATUS.same),
    },
].map((c) => (c.key === 'hjNow' ? { ...c, group: 'Check' } : c));

const INV_CHECK = [
    deltaCol({ key: 'jdeDelta', label: 'JDE − Ticket', w: 64, group: 'Check', get: (r) => (r.jdeDelta == null ? '' : r.jdeDelta), cell: (r) => <span style={{ color: '#991b1b', fontWeight: 700 }}>{fmtDelta(r.jdeDelta)}</span> }, (r) => r.jdeDelta, '#991b1b'),
    {
        key: 'check', label: 'Invoice Check', w: 126, group: 'Check', get: (r) => (r.jdeCc ? `${r.jdeStatus} (JDE ${r.jdeCc})` : r.jdeStatus),
        cell: (r) => <span title={r.jdeCc ? `JDE posted these hours on ${r.jdeCc}` : ''}><Pill label={r.jdeStatus} tone={JDE_TONE[r.jdeStatus]} /></span>,
        agg: checkAgg((r) => r.jdeStatus, JDE_TONE, JDE_STATUS.match),
    },
];
// The view: both checks; scoped to one summary, the invoice check alone.
const ALL_COLUMNS = [...KEY_COLUMNS, ...FT_CHECK, ...INV_CHECK];
const INV_COLUMNS = [...KEY_COLUMNS, ...INV_CHECK];

// ── On screen ──
// The grid reads the key trimmed; Export to Excel keeps every sheet column.
// Day and Type lead: Day labels the week and day lines grouped, and
// is the row's work date flat — the grid sorts on it. The card key, JDE key
// and Date Worked stay in the export (grouped by JDE key the JDE key shows:
// it is what the lines group on). A person ("Name - ID") and a unit ("ID -
// Description") share one column; a tier (its description, not the JDE code)
// → the rate sheet row it bills as shares the next with a title → its billing
// classification; JDE ST / OT fold under JDE Total (and the invoice's
// under Invoice Hours) until opened, and
// each status's three signers (email, date) under the status. The statuses
// move up to follow Day and Type.
const DAY_W = 96;
const STATUS_GROUPS = ['Daily', 'Weekly', 'Invoice'];
const pair = (a, b) => (a && b && a !== b ? `${a} → ${b}` : a || b || '');
const empLabel = (r) => (r.k === 'Labor' ? [r.name, r.emp].filter(Boolean).join(' - ') : '');
const unitLabel = (r) => {
    if (r.k !== 'Equipment') return '';
    const id = equipId(r);
    const desc = String(r.unitDesc || '').trim();
    return desc && ![normCode(id), normCode(r.unit)].includes(normCode(desc)) ? `${id} - ${desc}` : id;
};
const headHint = { display: 'block', fontSize: 9, fontWeight: 600, color: '#1e40af', marginTop: 2 };

// `amount` is the dollar column that follows Invoice Hours — the Invoice
// Amount scoped to one invoice (the Invoices view), the Invoiced Amount over
// every summary on the tab — and `onClass` makes a row's billing
// class a click to adjust it ({ can(r), open(r) }).
const screenColumns = (base, { grouped, groupBy, newest, onSort, jdeSplit, onSplit, sigOpen, onSigs, tierName, amount, onClass, onReopen }) => {
    const day = {
        key: 'day', label: 'Day', w: DAY_W, frozen: 0, get: (r) => r.d,
        // Grouped, the day line above carries the date; the row keeps its crew.
        cell: (r) => (
            <span>
                {grouped ? null : fmtDate(r.d)}
                {r.sh === 'Night' ? <span style={{ color: '#64748b', marginLeft: grouped ? 0 : 4 }} title="Night crew">{grouped ? 'Night' : 'N'}</span> : null}
            </span>
        ),
        head: () => <span title={newest ? 'Newest first — click for oldest first' : 'Oldest first — click for newest first'}>{`Day ${newest ? '↓' : '↑'}`}</span>,
        onHead: onSort,
    };
    // A person and a unit share one column, as a title → billing class and a
    // tier → billing tier share the next: the row's Type says which it is.
    const isEq = (r) => r.k === 'Equipment';
    const whoOf = (r) => (isEq(r) ? unitLabel(r) : empLabel(r));
    const classOf = (r) => (isEq(r) ? pair(tierName(r.eqTier), r.eqBill) : pair(r.title, r.cls));
    const classTip = (r) => (isEq(r) ? (r.eqTier ? `JDE tier ${r.eqTier}${r.eqBill ? ` bills as ${r.eqBill}` : ''}` : '') : classOf(r));
    const whoCount = (rows) => {
        const n = (k, f) => new Set(rows.filter((r) => r.k === k).map(f).filter(Boolean)).size;
        const people = n('Labor', empLabel);
        const units = n('Equipment', unitLabel);
        return [people ? plural(people, 'person', 'people') : '', units ? plural(units, 'unit', 'units') : ''].filter(Boolean).join(' · ');
    };
    const splitHead = (c) => () => <span>{c.label}<span style={headHint}>{jdeSplit ? '− ST / OT' : '+ ST / OT'}</span></span>;
    const cols = {
        id: [day],
        type: (c) => [{ ...c, frozen: DAY_W }],
        card: [],
        jdeKey: (c) => (groupBy === 'jde' ? [c] : []),
        d: [],
        eqId: [],
        eqTier: [],
        eqBill: [],
        emp: [{
            key: 'who', label: 'Employee / Equipment', w: 230, get: whoOf,
            cell: (r) => <span title={isEq(r) ? r.unitDesc || '' : empLabel(r)}>{whoOf(r)}</span>,
            many: (vals, rows) => whoCount(rows),
        }],
        name: [],
        title: [{
            key: 'class', label: 'Title / Tier → Billing Class', w: 240, get: classOf,
            cell: (r) => (onClass && onClass.can(r) ? (
                <span
                    role="button"
                    title={`${classTip(r) ? `${classTip(r)} — ` : ''}click to adjust the classification this bills as`}
                    onClick={(e) => { e.stopPropagation(); onClass.open(r); }}
                    style={{ color: '#1e40af', cursor: 'pointer' }}
                >
                    {`${classOf(r) || '—'} ✎`}
                </span>
            ) : <span title={classTip(r)}>{classOf(r)}</span>),
        }],
        // Invoice ST / OT fold under Invoice Hours on the same toggle as
        // JDE's, so the two splits open side by side.
        invSt: [],
        invOt: [],
        invHrs: (c) => [
            ...(jdeSplit ? base.filter((x) => x.key === 'invSt' || x.key === 'invOt') : []),
            { ...c, head: splitHead(c), onHead: onSplit },
            ...(amount ? [amount] : []),
        ],
        cls: [],
        jdeSt: (c) => (jdeSplit ? [c] : []),
        jdeOt: (c) => (jdeSplit ? [c] : []),
        jde: (c) => [{ ...c, head: splitHead(c), onHead: onSplit }],
        // A check reads Reviewed on a reviewed row (the review's note and
        // ✕ beside the Invoice Check), with the group's count leaving
        // reviewed rows out.
        status: (c) => [{
            ...c,
            cell: (r) => (r.rv ? <ReviewCell r={r} side="ticket" tone={HJ_TONE} brief /> : c.cell(r)),
            agg: checkAgg((r) => reviewStatus(r, 'ticket') || r.status, HJ_TONE, HJ_STATUS.same),
        }],
        check: (c) => [{
            ...c,
            cell: (r) => (r.rv ? <ReviewCell r={r} side="invoice" tone={JDE_TONE} onReopen={onReopen} /> : c.cell(r)),
            agg: checkAgg((r) => reviewStatus(r, 'invoice') || r.jdeStatus, JDE_TONE, JDE_STATUS.match),
        }],
    };
    ['daily', 'weekly', 'invoice'].forEach((level) => {
        const signer = (c) => (sigOpen[level] ? [c] : []);
        cols[`${level}Status`] = (c) => [{
            ...c,
            head: () => <span>{c.label}<span style={headHint}>{sigOpen[level] ? '− Signers' : '+ Signers'}</span></span>,
            onHead: () => onSigs(level),
        }];
        [0, 1, 2].forEach((i) => { cols[`${level}${i}e`] = signer; cols[`${level}${i}t`] = signer; });
    });
    const out = base.flatMap((c) => {
        const m = cols[c.key];
        return m === undefined ? [c] : typeof m === 'function' ? m(c) : m;
    });
    // The Daily / Weekly / Invoice statuses come right after Day and Type.
    const isStatus = (c) => STATUS_GROUPS.includes(c.group);
    // Per diem follows the hour columns (after Invoice Hours), ahead of the
    // Check band.
    const isPd = (c) => c.key === 'hjPd' || c.key === 'jdePd';
    const rest = out.filter((c) => c.frozen == null && !isStatus(c) && !isPd(c));
    const at = rest.findIndex((c) => c.key === (amount ? amount.key : 'invHrs')) + 1;
    return [
        ...out.filter((c) => c.frozen != null),
        ...out.filter(isStatus),
        ...rest.slice(0, at),
        ...out.filter(isPd),
        ...rest.slice(at),
    ];
};

// Who and what a row is: a week or day row counts these ("1 person") even
// when every row under it shares one.
const PER_ROW = new Set(['cc', 'who', 'class']);

// One cell of a week / day / person row, off the rows under it.
const groupCell = (c, rows, level) => {
    if (c.agg) return c.agg(rows);
    if (c.sum) {
        const vals = rows.map(c.sum).filter((v) => v != null && v !== '');
        if (!vals.length) return c.sumBlank ?? '—';
        const t = r2(vals.reduce((a, v) => a + num(v), 0));
        return c.sumCell ? c.sumCell(t) : fmtH(t);
    }
    const vals = [...new Set(rows.map((r) => String(c.get(r) ?? '')))];
    if (vals.length === 1 && !(level < 2 && PER_ROW.has(c.key))) return c.cell ? c.cell(rows[0]) : c.get(rows[0]);
    return c.many ? c.many(vals.filter(Boolean).sort(), rows) : '';
};

// readHeavyJobCards takes at most 31 days a call, so All weeks reads the
// cards four weeks at a time.
const cardSpans = (start, end) => {
    const out = [];
    const last = moment(end, 'YYYY-MM-DD');
    for (let s = moment(start, 'YYYY-MM-DD'); !s.isAfter(last); s = moment(s).add(28, 'days')) {
        out.push({ start: s.format('YYYY-MM-DD'), end: moment.min(moment(s).add(27, 'days'), last).format('YYYY-MM-DD') });
    }
    return out;
};

// ── Dates ──
// The grid reads one span of work dates. Weeks run Monday–Sunday, the
// tickets' weeks; the presets count from today.
const iso = (m) => m.format('YYYY-MM-DD');
const weekOf = (we) => ({ start: iso(moment(we, 'YYYY-MM-DD').subtract(6, 'days')), end: we });
const thisWe = () => weekEnding(iso(moment()));
const DATE_PRESETS = [
    { id: 'this', label: 'This week', span: () => weekOf(thisWe()) },
    { id: 'last', label: 'Last week', span: () => weekOf(iso(moment(thisWe(), 'YYYY-MM-DD').subtract(7, 'days'))) },
    { id: 'four', label: 'Last 4 weeks', span: () => ({ start: iso(moment(thisWe(), 'YYYY-MM-DD').subtract(27, 'days')), end: thisWe() }) },
    { id: 'month', label: 'This month', span: () => ({ start: iso(moment().startOf('month')), end: iso(moment().endOf('month')) }) },
    {
        id: 'lastMonth', label: 'Last month',
        span: () => ({ start: iso(moment().subtract(1, 'month').startOf('month')), end: iso(moment().subtract(1, 'month').endOf('month')) }),
    },
];
const sameSpan = (a, b) => Boolean(a && b && a.start === b.start && a.end === b.end);
// Monday to Sunday, whole weeks.
const weekAligned = (s) => moment(s.start, 'YYYY-MM-DD').day() === 1 && moment(s.end, 'YYYY-MM-DD').day() === 0;
// A span as the export names it: WE 9/27/26, WE 8/30/26–9/27/26, or the dates.
const spanLabel = (s, f) => {
    const fmt = (d) => moment(d, 'YYYY-MM-DD').format(f);
    if (sameSpan(s, weekOf(s.end))) return `WE ${fmt(s.end)}`;
    return weekAligned(s) ? `WE ${fmt(weekEnding(s.start))}–${fmt(s.end)}` : `${fmt(s.start)}–${fmt(s.end)}`;
};
// ── Look ──
// The bars are plain elements styled here (hover and focus want real CSS):
// a segmented track, pill toggles, quiet buttons and fields.
const CSS = `
.rc-row { display: flex; align-items: center; flex-wrap: wrap; gap: 8px; }
.rc-gap { flex-grow: 1; }
.rc-title { font-size: 15px; font-weight: 700; color: #0f172a; letter-spacing: -0.01em; }
.rc-lbl { font-size: 11px; font-weight: 600; color: #94a3b8; margin-left: 4px; }
.rc-muted { font-size: 11px; color: #94a3b8; }
.rc-seg { display: inline-flex; padding: 2px; border-radius: 8px; background: #f1f5f9; }
.rc-seg button { border: 0; background: transparent; font: inherit; font-size: 12px; font-weight: 500; line-height: 16px; color: #64748b; padding: 4px 10px; border-radius: 6px; cursor: pointer; white-space: nowrap; }
.rc-seg button:hover { color: #0f172a; }
.rc-seg button.on { background: #ffffff; color: #0f172a; font-weight: 600; box-shadow: 0 1px 2px rgba(15, 23, 42, 0.1), 0 0 0 1px rgba(15, 23, 42, 0.04); }
.rc-pill { border: 1px solid #e2e8f0; background: #ffffff; font: inherit; font-size: 12px; font-weight: 500; line-height: 16px; color: #475569; padding: 4px 11px; border-radius: 999px; cursor: pointer; white-space: nowrap; }
.rc-pill:hover { border-color: #cbd5e1; color: #0f172a; }
.rc-pill.on { background: #0f172a; border-color: #0f172a; color: #ffffff; }
.rc-btn { border: 1px solid #e2e8f0; background: #ffffff; font: inherit; font-size: 12px; font-weight: 600; line-height: 16px; color: #0f172a; padding: 5px 12px; border-radius: 7px; cursor: pointer; white-space: nowrap; }
.rc-btn:hover:not(:disabled) { background: #f8fafc; border-color: #cbd5e1; }
.rc-btn:disabled { opacity: 0.4; cursor: default; }
.rc-btn.step { padding: 5px 9px; color: #475569; }
.rc-btn.link { border-color: transparent; background: transparent; color: #1e40af; padding: 5px 6px; }
.rc-field { height: 28px; box-sizing: border-box; border: 1px solid #e2e8f0; border-radius: 7px; background: #ffffff; font: inherit; font-size: 12px; color: #0f172a; padding: 0 8px; }
.rc-field:hover { border-color: #cbd5e1; }
.rc-field:focus { outline: none; border-color: #94a3b8; box-shadow: 0 0 0 3px rgba(148, 163, 184, 0.2); }
.rc-sends { display: flex; flex-wrap: wrap; gap: 6px; }
.rc-send { font-size: 11px; color: #475569; background: #f8fafc; border: 1px solid #eef2f6; border-radius: 6px; padding: 2px 8px; white-space: nowrap; cursor: default; }
.rc-kpis { display: flex; flex-wrap: wrap; border: 1px solid #eef2f6; border-radius: 10px; background: #fbfcfd; }
.rc-kpi { padding: 9px 18px; border-left: 1px solid #eef2f6; }
.rc-kpi:first-child { border-left: 0; }
.rc-kpi-l { font-size: 10px; font-weight: 600; letter-spacing: 0.05em; text-transform: uppercase; color: #94a3b8; white-space: nowrap; }
.rc-kpi-v { margin-top: 2px; font-size: 18px; font-weight: 700; line-height: 22px; color: #0f172a; font-variant-numeric: tabular-nums; white-space: nowrap; }
.rc-kpi-s { margin-left: 6px; font-size: 11px; font-weight: 500; color: #94a3b8; }
.rc-pick { padding: 8px 12px; background: #f8fafc; border: 1px solid #e2e8f0; border-radius: 8px; }
`;
// One choice of a few: a track with the picked option raised.
const Seg = ({ label, options, value, onPick }) => (
    <div className="rc-seg" role="group" aria-label={label}>
        {options.map((o) => (
            <button key={String(o.value)} type="button" className={o.value === value ? 'on' : ''} aria-pressed={o.value === value} onClick={() => onPick(o.value)}>{o.label}</button>
        ))}
    </div>
);
const Toggle = ({ label, on, onPress }) => (
    <button type="button" className={`rc-pill${on ? ' on' : ''}`} aria-pressed={on} onClick={onPress}>{label}</button>
);
const Btn = ({ label, onPress, disabled, kind, title, aria }) => (
    <button type="button" className={`rc-btn${kind ? ` ${kind}` : ''}`} onClick={onPress} disabled={disabled} title={title} aria-label={aria}>{label}</button>
);

const GROUP_BY = [{ id: 'who', label: 'Person / equipment' }, { id: 'jde', label: 'JDE key' }, { id: 'none', label: 'None' }];
// Week and day rows get a tint; a person (or JDE key) row reads like a row.
const GROUP_BG = ['#e9eef5', '#f4f6f9', '#ffffff'];

// The header band: runs of columns sharing a group, blank between them.
const groupSpans = (cols) => cols.reduce((out, c) => {
    const g = c.group || '';
    const frozen = c.frozen != null;
    const last = out[out.length - 1];
    if (last && last.g === g && last.frozen === frozen) { last.n += 1; last.w += c.w; } else out.push({ g, n: 1, key: c.key, frozen, w: c.w });
    return out;
}, []);

// Fitting the grid to the card. A column's room is its `w` plus the cell
// padding; short of room, each gives up its slack down to a floor that keeps
// it legible (a pill, an hour figure, the job number), so the wide name and
// class columns take most of the squeeze. Widths come back as percentages of
// the table; with no width measured yet (or room to spare) they keep `w`'s
// proportions.
const CELL_PAD = 12;
const MIN_W = { tick: 30, day: 84, type: 62, job: 66, cc: 56, who: 110, class: 110, status: 92, check: 92, invAmt: 96, act: 36 };
const minWidth = (c) => Math.min(c.w + CELL_PAD, MIN_W[c.key]
    ?? (STATUS_GROUPS.includes(c.group) ? (/Status$/.test(c.key) ? 72 : 56) : c.right ? 44 : 60));
const fitWidths = (cols, room) => {
    const nat = cols.map((c) => c.w + CELL_PAD);
    const total = nat.reduce((a, n) => a + n, 0) || 1;
    if (!room || total <= room) return nat.map((n) => n / total);
    const min = cols.map(minWidth);
    const slack = nat.map((n, i) => n - min[i]);
    const give = Math.min(1, (total - room) / (slack.reduce((a, s) => a + s, 0) || 1));
    const out = nat.map((n, i) => n - slack[i] * give);
    const sum = out.reduce((a, n) => a + n, 0);
    return out.map((n) => n / sum);
};

const ROW_CAP = 1500;

// The tickets a set of picked days can open as (billingTicketMap's openHandoff).
const CREATE = [
    { kind: 'day', label: 'Day field ticket', ok: (dates) => dates.length === 1, why: 'pick one day' },
    { kind: 'combined', label: 'Combined days ticket', ok: (dates) => dates.length > 1, why: 'pick two or more days' },
    { kind: 'summary', label: 'Weekly summary', ok: (dates) => new Set(dates.map(weekEnding)).size === 1, why: 'pick days in one week' },
];

const BillingReconciliation = ({
    jobKey, txns, costLoading, envelopes, invoices, invTotals, equipTiers, userName, userEmail,
    ticketDocs,     // parseDocs(userRows): a week summary on its own is its days' weekly ticket
    onCreateTicket, // ({ kind, cells }) => void — opens Daily Field Tickets' Ticket Map on the days
    onCreateInvoice, // (weekEnds) => void — opens Invoice Workbook with the picked weeks and builds the invoice folder
    invoiceDrafts,  // parseInvoiceDrafts(userRows): the drafts already built, so a picked week says so
    // Scoped to one billing summary (the Invoices view's opened invoice):
    // its Invoice tab over the rows its lines sit on, the dates those lines
    // span, with what the summary bills for each row. No date bar, HeavyJob
    // read, Record or ticket picking — the grid, its filters and export.
    invoice,        // the summary number, or unset for the Labor & Equipment tab
    onReclass,      // (lines) => void — adjust the class these labor lines bill as (open summaries)
    onRemove,       // (lines) => void — take these lines off the summary (not yet sent)
}) => {
    // The filter picks survive a refresh (usePersistedState); the dates and
    // the search save per job. An invoice's own grid starts unfiltered each
    // time it opens and leaves the tab's picks alone.
    // Scoped to a summary only the invoice check runs; on the tab both do.
    const hj = !invoice;
    const [snaps, setSnaps] = useState({ job: null, loading: false, error: '', list: [] });
    // The work dates read ({ start, end }); null: the newest week a ticket went out.
    const [picked, setPicked] = usePersistedState(`billing.${jobKey}.recon.picked`, null);
    const [newest, setNewest] = usePersistedState('billing.recon.newest', true);
    const [jdeSplit, setJdeSplit] = usePersistedState('billing.recon.jdeSplit', false);
    // The status levels whose signer columns are open (all folded to start).
    const [sigOpen, setSigOpen] = useState({});
    const [stored, setStored] = useState({ key: '', loading: false, error: '', rows: [] });
    const [live, setLive] = useState({ key: '', loading: false, error: '', data: [], cards: [], at: null });
    const [mode, setMode] = usePersistedState('billing.recon.mode', 'latest');
    const kindTab = usePersistedState('billing.recon.kind', 'All');
    const kindInv = useState('All');
    const [kind, setKind] = invoice ? kindInv : kindTab;
    const issuesTab = usePersistedState('billing.recon.issuesOnly', false);
    const issuesInv = useState(false);
    const [issuesOnly, setIssuesOnly] = invoice ? issuesInv : issuesTab;
    // Not persisted: billed rows start hidden every visit.
    const [showBilled, setShowBilled] = useState(false);
    const tol = RECON_VARIANCE;
    const qTab = usePersistedState(`billing.${jobKey}.recon.q`, '');
    const qInv = useState('');
    const [q, setQ] = invoice ? qInv : qTab;
    const [showAll, setShowAll] = useState(false);
    const [recording, setRecording] = useState(false);
    // Days ticked for a new ticket (the Create bar): date -> true.
    const [pickedDays, setPickedDays] = useState({});
    // Rows ticked to review (the Review bar): reviewKey -> true, the note
    // they'll carry, and the reviews stored for the span (reviewMap).
    const [ticked, setTicked] = useState({});
    const [note, setNote] = useState('');
    const [reviewing, setReviewing] = useState(false);
    const [reviews, setReviews] = useState({ key: '', map: {} });
    // Grouping: week > day > person/unit (or JDE key). `level` is how deep the
    // groups start open (null: to the people for one week, to the days for
    // All weeks); `flip` holds the groups clicked the other way.
    const [groupBy, setGroupBy] = usePersistedState('billing.recon.groupBy', 'who');
    const [expand, setExpand] = useState({ level: null, flip: {} });
    // The filter bar and the grid's header follow the page down as it
    // scrolls, the header snug under the bar, both parked at the grid's last
    // row (as billing.js's Third Party grid does). position:sticky can't: it
    // pins against the nearest scroll container, and the Billing page's
    // ScrollView is one that grows with its content while the page around it
    // scrolls. So a document-level scroll listener (capture — scroll events
    // don't bubble) translates both by however far they've gone past the top
    // edge, reading the applied offset back off each element.
    const barRef = useRef(null);
    const headRef = useRef(null);
    const syncRef = useRef(null);
    useEffect(() => {
        const shifted = (el) => {
            const m = /translateY\((-?[\d.]+)px\)/.exec(el.style.transform || '');
            return m ? parseFloat(m[1]) : 0;
        };
        const put = (el, y) => {
            if (shifted(el) !== y) el.style.transform = y ? `translateY(${y}px)` : '';
        };
        const sync = () => {
            const bar = barRef.current;
            const head = headRef.current;
            if (!bar || !head) return;
            // The pinning edge: the nearest ancestor that genuinely scrolls,
            // else the viewport top (the window scrolling).
            let edge = 0;
            for (let p = bar.parentElement; p && p !== document.documentElement; p = p.parentElement) {
                const o = window.getComputedStyle(p).overflowY;
                if ((o === 'auto' || o === 'scroll') && p.scrollHeight > p.clientHeight + 1) {
                    edge = p.getBoundingClientRect().top;
                    break;
                }
            }
            const barH = bar.offsetHeight;
            const barTop = bar.getBoundingClientRect().top - shifted(bar);
            const headTop = head.getBoundingClientRect().top - shifted(head);
            const parkAt = Math.max(0, head.parentElement.offsetHeight - head.offsetHeight);
            const headY = Math.min(parkAt, Math.max(0, Math.round(edge + barH - headTop)));
            put(head, headY);
            put(bar, Math.max(0, Math.min(headY + Math.round(headTop - barTop) - barH, Math.round(edge - barTop))));
        };
        syncRef.current = sync;
        document.addEventListener('scroll', sync, true);
        window.addEventListener('resize', sync);
        return () => {
            syncRef.current = null;
            document.removeEventListener('scroll', sync, true);
            window.removeEventListener('resize', sync);
        };
    }, []);
    // Rows opening, closing or filtering move the grid's end under a
    // scrolled page — re-place the two after every render.
    useEffect(() => { if (syncRef.current) syncRef.current(); });
    // The grid's width, for fitting its columns (fitWidths).
    const [gridW, setGridW] = useState(0);
    const gridObs = useRef(null);
    const gridBoxRef = useCallback((el) => {
        if (gridObs.current) gridObs.current();
        gridObs.current = null;
        if (!el) return;
        const read = () => setGridW((w) => (w === el.clientWidth ? w : el.clientWidth));
        read();
        if (typeof ResizeObserver === 'undefined') {
            window.addEventListener('resize', read);
            gridObs.current = () => window.removeEventListener('resize', read);
        } else {
            const ro = new ResizeObserver(read);
            ro.observe(el);
            gridObs.current = () => ro.disconnect();
        }
    }, []);

    const loadSnaps = (job) => {
        setSnaps((p) => ({ job, loading: true, error: '', list: p.job === job ? p.list : [] }));
        Promise.resolve()
            .then(() => fieldTicketReconSnapshots({ job: String(job) }))
            .then((res) => setSnaps((p) => (p.job !== job ? p : !res || res.error
                ? { job, loading: false, error: res?.error ? String(res.error) : 'no response from the server (is fieldTicketReconciliation deployed here?)', list: [] }
                : { job, loading: false, error: '', list: res.snapshots || [] })))
            .catch((e) => setSnaps((p) => (p.job === job ? { job, loading: false, error: String(e?.message || e), list: [] } : p)));
    };
    // `picked` is saved per job, so a job switch already lands on that job's
    // own dates.
    useEffect(() => {
        if (jobKey && !invoice) loadSnaps(jobKey);
    }, [jobKey, invoice]);

    // Scoped to a summary: its lines, the ones this grid holds (jdeClaims —
    // the rest are the Invoices view's Third Party lines), and the dates
    // those span. `invJk` finds a row's lines by JDE key, so per diem rides
    // along with the row it sits on.
    const invLines = useMemo(() => (invoice ? (txns || []).filter((t) => t.inv === invoice) : []), [invoice, txns]);
    const invSpan = useMemo(() => {
        const ds = invLines.filter(jdeClaims).map((t) => t.date).sort();
        return { start: ds[0] || '', end: ds[ds.length - 1] || '' };
    }, [invLines]);
    const invJk = useMemo(() => {
        const m = {};
        invLines.forEach((t) => {
            const k = jdeKeyOf(t);
            if (k) (m[k] = m[k] || []).push(t);
        });
        return m;
    }, [invLines]);
    // What the summary bills per line, off its frozen snapshot.
    const invBill = useMemo(() => {
        const m = {};
        (invTotals?.[invoice]?.lines || []).forEach((l) => {
            if (l.key) m[l.key] = l.b != null ? num(l.b) : num(l.c) * (1 + num(l.m) / 100);
        });
        return m;
    }, [invTotals, invoice]);
    // On the tab (no summary in scope): a row's billed JDE lines across every
    // summary (drafts included; per diem found by JDE key, as invScoped does)
    // and what they bill off the frozen lines — Invoice Hours in dollars, so
    // a partly billed row shows how much has already gone out.
    const invoicedOf = useMemo(() => {
        if (invoice) return null;
        const amt = {};
        Object.values(invTotals || {}).forEach((v) => (v.lines || []).forEach((l) => {
            if (l.key) amt[l.key] = l.b != null ? num(l.b) : num(l.c) * (1 + num(l.m) / 100);
        }));
        const byJk = {};
        (txns || []).forEach((t) => {
            if (!t.inv) return;
            const k = jdeKeyOf(t);
            if (k) (byJk[k] = byJk[k] || []).push(t);
        });
        const cache = new WeakMap();
        return (r) => {
            if (cache.has(r)) return cache.get(r);
            const seen = new Map();
            (r.jdeLines || []).forEach((t) => { if (t.inv) seen.set(t.key, t); });
            (r.jdeKeys || []).forEach((k) => (byJk[k] || []).forEach((t) => seen.set(t.key, t)));
            const lines = [...seen.values()];
            const lineAmt = (t) => amt[t.key] ?? num(t.bill);
            const out = { lines, lineAmt, amt: lines.length ? r2(lines.reduce((a, t) => a + lineAmt(t), 0)) : null };
            cache.set(r, out);
            return out;
        };
    }, [invoice, invTotals, txns]);

    const weeks = useMemo(() => snapshotWeeks(snaps.list, weekEnding), [snaps.list]);
    // All dates reads every week a ticket went out.
    const allSpan = weeks.length ? { start: weekOf(weeks[weeks.length - 1].we).start, end: weeks[0].we } : null;
    const span = invoice ? invSpan : picked || (weeks[0] ? weekOf(weeks[0].we) : { start: '', end: '' });
    const { start, end } = span;
    const multiWeek = Boolean(end) && weekEnding(start) !== weekEnding(end);
    const rangeKey = `${jobKey}|${start}|${end}`;
    const pickSpan = (s) => { setPicked(s); setShowAll(false); };
    // From past To (or To before From) moves the other end with it.
    const setFrom = (v) => { if (v) pickSpan({ start: v, end: end && end >= v ? end : v }); };
    const setTo = (v) => { if (v) pickSpan({ start: start && start <= v ? start : v, end: v }); };

    const loadStored = () => {
        if (!jobKey || !end) return;
        const key = rangeKey;
        setStored((p) => ({ key, loading: true, error: '', rows: p.key === key ? p.rows : [] }));
        Promise.resolve()
            .then(() => fieldTicketReconRead({ job: String(jobKey), start, end }))
            .then((res) => setStored((p) => (p.key !== key ? p : !res || res.error
                ? { key, loading: false, error: res?.error ? String(res.error) : 'no response from the server', rows: [] }
                : { key, loading: false, error: '', rows: (res.rows || []).map(fromDb) })))
            .catch((e) => setStored((p) => (p.key === key ? { key, loading: false, error: String(e?.message || e), rows: [] } : p)));
    };
    const loadReviews = () => {
        if (!jobKey || !end) return;
        const key = rangeKey;
        Promise.resolve()
            .then(() => fieldTicketReconReviews({ job: String(jobKey), start, end }))
            .then((res) => setReviews((p) => (p.key === key && !res?.reviews && !res?.error ? p : { key, map: reviewMap(res?.reviews) })))
            .catch(() => setReviews((p) => (p.key === key ? p : { key, map: {} })));
    };
    useEffect(() => { loadStored(); loadReviews(); setPickedDays({}); setTicked({}); }, [rangeKey]); // re-read when the job or week changes

    // The cards as they stand now, for the HeavyJob check — one call per
    // four weeks of the span.
    const loadLive = () => {
        if (!jobKey || !end) return;
        const key = rangeKey;
        setLive((p) => ({ key, loading: true, error: '', data: p.key === key ? p.data : [], cards: p.key === key ? p.cards : [], at: p.key === key ? p.at : null }));
        Promise.all(cardSpans(start, end).map((s) => Promise.resolve().then(() => loadHeavyJobCards({ job: String(jobKey), ...s }))))
            .then((list) => {
                const bad = list.findIndex((x) => !x || x.error || x.configured === false);
                const res = list[bad];
                setLive((p) => (p.key !== key ? p : bad >= 0
                    ? { key, loading: false, error: res?.error ? String(res.error) : !res ? 'no response from the server' : 'HeavyJob API not configured here', data: [], cards: [], at: null }
                    : {
                        key, loading: false, error: '',
                        data: list.flatMap((x) => x.data || []), cards: list.flatMap((x) => x.cards || []),
                        at: list[0]?.fetchedAt || new Date().toISOString(),
                    }));
            })
            .catch((e) => setLive((p) => (p.key === key ? { key, loading: false, error: String(e?.message || e), data: [], cards: [], at: null } : p)));
    };
    useEffect(() => {
        if (hj && end && live.key !== rangeKey) loadLive();
    }, [hj, rangeKey]); // fetch the cards the first time the tab shows a span

    // What the three statuses are read from: the job's envelopes (recipients
    // nested) and its billing summaries with the work dates their lines span.
    const summaries = useMemo(() => (invoices || []).map((v) => {
        const dates = (invTotals?.[v.no]?.lines || []).map((l) => String(l.d || '').slice(0, 10)).filter(Boolean).sort();
        return { no: v.no, status: v.status, first: dates[0] || '', last: dates[dates.length - 1] || '' };
    }), [invoices, invTotals]);
    const ctx = useMemo(() => statusContext({ envelopes, summaries, weekEnding, docs: ticketDocs }), [envelopes, summaries, ticketDocs]);
    // Each billed line's hours as its summary froze them, by cost-history key.
    const billedHrs = useMemo(() => {
        const m = {};
        Object.values(invTotals || {}).forEach((t) => (t.lines || []).forEach((l) => { if (l.key && l.hr != null) m[l.key] = num(l.hr); }));
        return m;
    }, [invTotals]);

    const current = useMemo(
        () => currentRows(stored.key === rangeKey ? stored.rows : [], mode),
        [stored, rangeKey, mode],
    );
    // The whole billing key for the span: the stored rows with their JDE
    // match (plus JDE lines nobody ticketed) and the three statuses.
    // No span, no rows: without one every JDE line on the job would come
    // back as "No field ticket".
    const keyRows = useMemo(
        () => (end ? matchJde(current, txns || [], { start, end, tol })
            .map((r) => ({ ...r, job: String(jobKey), st: statusesOf(r, ctx), ...invoiceSplit(r, billedHrs) })) : []),
        [current, txns, start, end, tol, ctx, jobKey, billedHrs],
    );
    // The job's own ticket rules (fold, names) on the live cards too, so they
    // key the way the stored rows do.
    const liveRows = useMemo(() => {
        if (live.key !== rangeKey) return [];
        const R = rulesFor(jobKey);
        return foldRows(applyPeople(live.data, R.people), R.fold).rows;
    }, [live, rangeKey, jobKey]);
    // Until the cards land (or when HeavyJob can't be read) the stored rows
    // show with the "now" side blank, rather than an empty grid. JDE lines no
    // ticket row holds ride along either way (the "No field ticket" rows).
    const liveReady = live.key === rangeKey && !live.loading && !live.error;
    // A row whose JDE lines are all on a billing summary (drafts included) is
    // done — it leaves the grid (Invoices holds it) unless Show billed brings
    // it back to check what the summary billed against the ticket. Partly
    // billed rows always stay. Record to table still stamps every row.
    const ftAll = useMemo(
        () => withJdeOnly(liveReady
            ? compareHeavyJob(keyRows.filter((r) => r.id), liveRows, live.cards, { tol }).map((r) => (r.id ? r : { ...r, job: String(jobKey) }))
            : keyRows.filter((r) => r.id).map((r) => ({ ...r, hjNow: null, delta: null, status: '', ticketGap: Math.abs(r2(r.ft) - r2(r.hj)) > RECON_TOL })), keyRows),
        [keyRows, liveRows, live, liveReady, jobKey, tol],
    );
    const ftRows = useMemo(() => ftAll.filter((r) => showBilled || r.billedNow !== 'Billed'), [ftAll, showBilled]);
    // Scoped to a summary, the rows its lines sit on — each with those lines
    // (`invLines`) and what the summary bills for them (`invAmt`).
    const invScoped = useMemo(() => {
        if (!invoice) return [];
        return keyRows.map((r) => {
            const seen = new Map();
            (r.jdeLines || []).forEach((t) => { if (t.inv === invoice) seen.set(t.key, t); });
            (r.jdeKeys || []).forEach((k) => (invJk[k] || []).forEach((t) => seen.set(t.key, t)));
            if (!seen.size) return null;
            const lines = [...seen.values()];
            return { ...r, invLines: lines, invAmt: r2(lines.reduce((a, t) => a + (invBill[t.key] ?? num(t.bill)), 0)) };
        }).filter(Boolean);
    }, [invoice, keyRows, invJk, invBill]);
    const invRows = useMemo(
        () => (invoice ? invScoped : keyRows.filter((r) => showBilled || r.billedNow !== 'Billed')),
        [invoice, invScoped, keyRows, showBilled],
    );
    const billedCount = invoice ? 0 : ftAll.filter((r) => r.billedNow === 'Billed').length;

    // An exception: either check, or JDE / the invoice billing other than
    // the ticket (offCells' red).
    // A reviewed row (neither check gone stale) is no exception: its cells
    // don't light and its bar stays off. A check that went stale flags on
    // its own.
    const sides = hj ? ['ticket', 'invoice'] : ['invoice'];
    const reviewed = (r) => sides.every((side) => reviewedOk(r, side));
    const offOf = (r) => (reviewed(r) ? {} : offCells(r, tol));
    const hjOff = (r) => hj && !reviewedOk(r, 'ticket') && r.status !== HJ_STATUS.same;
    const jdeOff = (r) => !reviewedOk(r, 'invoice') && r.jdeStatus !== JDE_STATUS.match;
    const statusTone = (r) => {
        if (reviewed(r)) return TONE.good;
        const tones = [hjOff(r) ? HJ_TONE[r.status] : null, jdeOff(r) ? JDE_TONE[r.jdeStatus] : null].filter(Boolean);
        return tones.find((t) => t === TONE.bad) || tones.find((t) => t === TONE.warn) || tones[0] || TONE.ok;
    };
    const isIssue = (r) => hjOff(r) || jdeOff(r)
        || (!reviewed(r) && Object.values(offCells(r, tol)).some((o) => o.tone === 'bad'));
    // How loud a row's left-edge bar is: its worst cell or its check.
    const rowFlag = (r, off) => worst([
        ...Object.values(off).map((o) => o.tone),
        statusTone(r) === TONE.bad ? 'bad' : statusTone(r) === TONE.warn ? 'warn' : '',
    ]);
    const reviewMapNow = reviews.key === rangeKey ? reviews.map : null;
    const allRows = useMemo(() => applyReviews(hj ? ftRows : invRows, reviewMapNow, { tol }), [hj, ftRows, invRows, reviewMapNow, tol]);

    // ── Reviewing rows ──
    // Tick rows, say why they differ, Mark reviewed: the review is stored
    // per row (PC.Field_Ticket_Recon_Review) and the row leaves Exceptions
    // only until a check moves again. ✕ on the pill reopens.
    const tickKeys = useMemo(() => Object.keys(ticked).filter((k) => ticked[k]), [ticked]);
    const toggleTick = (rows, on) => setTicked((p) => {
        const next = { ...p };
        rows.forEach((r) => { const k = reviewKey(r); if (on) next[k] = true; else delete next[k]; });
        return next;
    });
    const tickBox = (rows, label) => {
        const on = rows.length > 0 && rows.every((r) => ticked[reviewKey(r)]);
        return (
            <input
                type="checkbox"
                checked={on}
                aria-label={label}
                onClick={(e) => e.stopPropagation()}
                onChange={() => toggleTick(rows, !on)}
                style={{ marginTop: 0, marginBottom: 0, marginLeft: 0, marginRight: 0, verticalAlign: 'middle', cursor: 'pointer' }}
            />
        );
    };
    const markReviewed = async () => {
        const rows = allRows.filter((r) => ticked[reviewKey(r)]);
        const text = note.trim();
        if (!rows.length || !text || reviewing) return;
        setReviewing(true);
        try {
            const res = await fieldTicketReconReview({ job: String(jobKey), by: userName || userEmail || '', rows: rows.map((r) => reviewPayload(r, text)) });
            if (!res) throw new Error('no response from the server');
            if (res.error) throw new Error(res.error);
            showToast(`Marked ${plural(rows.length, 'row', 'rows')} reviewed.`, 'success');
            setTicked({});
            setNote('');
            loadReviews();
        } catch (e) {
            showToast(`Review failed — ${e?.message || e}`, 'error');
        } finally {
            setReviewing(false);
        }
    };
    const reopen = async (r) => {
        try {
            const res = await fieldTicketReconUnreview({ job: String(jobKey), keys: [reviewKey(r)] });
            if (!res) throw new Error('no response from the server');
            if (res.error) throw new Error(res.error);
            loadReviews();
        } catch (e) {
            showToast(`Reopen failed — ${e?.message || e}`, 'error');
        }
    };

    // ── Picking days for a ticket ──
    // Tick a week or a day (a row, ungrouped); Create hands the days to Daily
    // Field Tickets' Ticket Map, which opens them as a Draft to review and
    // send there. A day goes with the crews its rows ran; a row with no crew
    // (JDE or HeavyJob time no ticket holds) counts as the day crew.
    const canPick = Boolean(onCreateTicket || onCreateInvoice);
    const pickDates = useMemo(() => Object.keys(pickedDays).filter((d) => pickedDays[d]).sort(), [pickedDays]);
    // The weeks the picked days fall in: an invoice bills whole weeks.
    const pickWeeks = useMemo(() => [...new Set(pickDates.map(weekEnding))].sort(), [pickDates]);
    const pickDrafts = useMemo(() => draftsOver(invoiceDrafts || [], pickWeeks), [invoiceDrafts, pickWeeks]);
    const pickInvoiced = useMemo(() => invoicedWeeks(envelopes || [], pickWeeks), [envelopes, pickWeeks]);
    const draftLine = (d) => {
        const rv = lastReview(d);
        const st = draftStatus(d, pickInvoiced);
        return `${d.weeks.map((we) => `WE ${md(we)}`).join(', ')} built ${d.at ? moment(d.at, 'YYYY-MM-DD HH:mm').format('M/D') : ''}${d.by ? ` by ${d.by}` : ''}: ${st}${rv && st !== 'Sent' ? ` (${rv.by}${rv.note ? `: ${rv.note}` : ''})` : ''}`;
    };
    const togglePick = (dates, on) => setPickedDays((p) => {
        const next = { ...p };
        dates.forEach((d) => { if (on) next[d] = true; else delete next[d]; });
        return next;
    });
    const createTicket = (kind) => {
        const cells = pickDates.flatMap((d) => {
            const crews = [...new Set(allRows.filter((r) => r.d === d).map((r) => r.sh).filter((s) => s === 'Day' || s === 'Night'))];
            return (crews.length ? crews : ['Day']).map((sh) => `${d}|${sh}`);
        });
        onCreateTicket({ kind, cells });
    };
    // The week / day line opens and closes on a click; its box only picks.
    const pickBox = (dates, label) => {
        const on = dates.length > 0 && dates.every((d) => pickedDays[d]);
        return (
            <input
                type="checkbox"
                checked={on}
                aria-label={label}
                onClick={(e) => e.stopPropagation()}
                onChange={() => togglePick(dates, !on)}
                style={{ marginTop: 0, marginBottom: 0, marginLeft: 0, marginRight: 6, verticalAlign: 'middle', cursor: 'pointer' }}
            />
        );
    };
    const shown = useMemo(() => {
        const needle = q.trim().toLowerCase();
        return allRows.filter((r) => (kind === 'All' || r.k === kind)
            && (!issuesOnly || isIssue(r))
            && (!needle || [r.emp, r.name, r.unit, r.unitDesc, r.cc, r.title, r.cls, r.eqTier, r.eqBill].some((v) => String(v || '').toLowerCase().includes(needle))));
    }, [allRows, kind, issuesOnly, q, hj, tol]); // isIssue reads hj and tol
    const grouped = groupBy !== 'none';
    // Tier code → description: the job's own cost rows first, then the JDE
    // tier catalog; a code neither knows shows as itself.
    const tierNames = useMemo(() => {
        const m = {};
        (equipTiers || []).forEach((t) => { if (t?.code) m[t.code] = t.descr || ''; });
        return m;
    }, [equipTiers]);
    const tierName = useMemo(() => (code) => (code ? tierDescr(code, tierNames[code]) || code : ''), [tierNames]);
    // Scoped to a summary: what it bills per row (hover for the lines), the
    // billing class a click to adjust while the summary is open to fixes,
    // and a remove at the row's end while it has not gone out.
    // The amount column's pixel width this render (set once the column
    // shares are fitted below, read when the cells render after that): under
    // ~92px cents no longer fit, so the cells print whole dollars — the hover
    // keeps every line to the cent.
    const amtTight = useRef(false);
    const amtMoney = (n) => (amtTight.current ? fmtMoney0(n) : fmtMoney(n));
    const amountCol = useMemo(() => (invoice ? {
        key: 'invAmt', label: 'Invoice Amount', w: 96, right: true,
        note: `What ${invoice} bills for the row's JDE lines, off the summary's frozen lines`,
        get: (r) => (r.invAmt == null ? '' : r.invAmt),
        cell: (r) => (r.invAmt == null ? '—' : (
            <span title={(r.invLines || []).map((t) => [
                md(t.date), t.ct, t.ct !== '51500' && num(t.hrs) ? `${fmtH(t.hrs)} hrs` : '', fmtMoney(invBill[t.key] ?? t.bill), t.ba ? `as ${t.ba}` : '',
            ].filter(Boolean).join(' · ')).join('\n')}
            >
                {amtMoney(r.invAmt)}
            </span>
        )),
        sum: (r) => r.invAmt, sumBlank: '—', sumCell: (t) => <span style={{ fontWeight: 700 }}>{amtMoney(t)}</span>,
    } : {
        // The tab: the same column over every summary, so a row still
        // carrying unbilled JDE lines says what has already been invoiced.
        key: 'invAmt', label: 'Invoiced Amount', w: 96, right: true,
        note: 'What the billing summaries (drafts included) bill for the row\'s JDE lines so far, off their frozen lines',
        get: (r) => (invoicedOf(r).amt == null ? '' : invoicedOf(r).amt),
        cell: (r) => {
            const v = invoicedOf(r);
            return v.amt == null ? '—' : (
                <span
                    style={{ color: '#1e40af' }}
                    title={v.lines.map((t) => [
                        md(t.date), t.ct, t.ct !== '51500' && num(t.hrs) ? `${fmtH(t.hrs)} hrs` : '', fmtMoney(v.lineAmt(t)), `on ${t.inv}`,
                    ].filter(Boolean).join(' · ')).join('\n')}
                >
                    {amtMoney(v.amt)}
                </span>
            );
        },
        sum: (r) => invoicedOf(r).amt, sumBlank: '—', sumCell: (t) => <span style={{ fontWeight: 700, color: '#1e40af' }}>{amtMoney(t)}</span>,
    }), [invoice, invBill, invoicedOf]); // amtMoney reads a ref
    // The sheet's columns (Export to Excel), with the tier's description.
    const sheetColumns = useMemo(() => [...(hj ? ALL_COLUMNS : INV_COLUMNS), ...(amountCol ? [amountCol] : [])]
        .map((c) => (c.key === 'eqTier' ? { ...c, get: (r) => (r.k === 'Equipment' ? tierName(r.eqTier) : '') } : c)), [hj, tierName, amountCol]);
    const onClass = useMemo(() => {
        if (!invoice || !onReclass) return null;
        const rateLines = (r) => (r.invLines || []).filter((t) => (t.ct === '51100' || t.ct === '51200') && t.jde);
        return { can: (r) => r.k === 'Labor' && rateLines(r).length > 0, open: (r) => onReclass(rateLines(r)) };
    }, [invoice, onReclass]);
    const columns = useMemo(() => [
        // The review tick: a row's box, a person row's for its rows, the
        // header's for every row the filters show.
        { key: 'tick', label: '', w: 22, frozen: 0, get: () => '', agg: () => '', cell: (r) => tickBox([r], `Tick ${who(r) || r.d}`) },
        ...screenColumns(hj ? ALL_COLUMNS : INV_COLUMNS, {
            grouped, groupBy, newest, onSort: () => setNewest((v) => !v), jdeSplit, onSplit: () => setJdeSplit((v) => !v),
            sigOpen, onSigs: (level) => setSigOpen((o) => ({ ...o, [level]: !o[level] })), tierName, amount: amountCol, onClass, onReopen: reopen,
        }),
        ...(invoice && onRemove ? [{
            key: 'act', label: '', w: 36, get: () => '', agg: () => '',
            cell: (r) => ((r.invLines || []).length ? (
                <span
                    role="button"
                    title={`Remove ${plural(r.invLines.length, 'line', 'lines')} from ${invoice} — back to Unbilled`}
                    onClick={(e) => { e.stopPropagation(); onRemove(r.invLines); }}
                    style={{ color: '#b91c1c', fontWeight: 700, cursor: 'pointer' }}
                >
                    ✕
                </span>
            ) : ''),
        }] : []),
    ], [hj, grouped, groupBy, newest, jdeSplit, sigOpen, tierName, amountCol, onClass, invoice, onRemove, ticked]); // tickBox reads ticked
    // Newest (or oldest) date first; a day's rows keep their ticket order.
    const ordered = useMemo(() => (newest ? shown.slice().sort((a, b) => String(b.d).localeCompare(String(a.d))) : shown), [shown, newest]);
    const tree = useMemo(() => (grouped ? groupRows(shown, groupBy, weekEnding, { newestFirst: newest }) : []), [shown, groupBy, grouped, newest]);

    // What the grid draws, top to bottom: group rows ({ g }) and rows ({ r,
    // depth }). A person (or JDE key) with one row shows just the row.
    const baseLevel = expand.level ?? (multiWeek ? 1 : 2);
    const isOpen = (g) => (g.level < baseLevel) !== Boolean(expand.flip[g.key]);
    const items = useMemo(() => {
        if (!grouped) return ordered.map((r) => ({ r, depth: 0 }));
        const out = [];
        tree.forEach((w) => {
            out.push({ g: w });
            if (!isOpen(w)) return;
            w.children.forEach((d) => {
                out.push({ g: d });
                if (!isOpen(d)) return;
                d.children.forEach((e) => {
                    if (e.rows.length === 1) { out.push({ r: e.rows[0], depth: 2 }); return; }
                    out.push({ g: e });
                    if (isOpen(e)) e.rows.forEach((r) => out.push({ r, depth: 3 }));
                });
            });
        });
        return out;
    }, [grouped, ordered, tree, expand, baseLevel]); // isOpen reads expand and baseLevel
    const toggle = (k) => setExpand((e) => ({ ...e, flip: { ...e.flip, [k]: !e.flip[k] } }));

    // The sends the span covers, newest first (a document straddling its
    // edge counts).
    const spanSnaps = useMemo(() => snaps.list.filter((s) => {
        const first = String(s.First_Date || '').slice(0, 10);
        const last = String(s.Last_Date || first).slice(0, 10);
        return first && first <= end && last >= start;
    }).sort((a, b) => Number(b.Last_RowID) - Number(a.Last_RowID)), [snaps.list, start, end]);
    const spanWeeks = end ? moment(weekEnding(end), 'YYYY-MM-DD').diff(moment(weekEnding(start), 'YYYY-MM-DD'), 'weeks') + 1 : 0;

    const exportXlsx = () => {
        const title = invoice ? `Field Ticket Reconciliation — Field ticket vs JDE, invoice ${invoice}`
            : 'Field Ticket Reconciliation — HeavyJob at ticket vs now, field ticket vs JDE';
        // Every row the filters keep, in the grid's order, open or not.
        const rows = grouped ? tree.flatMap((w) => w.children.flatMap((d) => d.children.flatMap((e) => e.rows))) : ordered;
        const aoa = [
            ['Labor & Equipment Billing Key'],
            [`${title} · Job ${jobKey} · ${spanLabel(span, 'M/D/YY')} · ${mode === 'first' ? 'first send' : 'latest send'} per day/shift · exported ${moment().format('M/D/YY h:mm a')}`],
            [],
            sheetColumns.map((c) => c.group || ''),
            sheetColumns.map((c) => c.label),
            ...rows.map((r) => sheetColumns.map((c) => c.get(r))),
        ];
        const ws = XLSX.utils.aoa_to_sheet(aoa);
        ws['!cols'] = sheetColumns.map((c) => ({ wch: Math.max(8, Math.round(c.w / 7)) }));
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, 'Reconciliation');
        XLSX.writeFile(wb, invoice
            ? `Field Ticket Reconciliation ${jobKey} ${invoice}.xlsx`
            : `Field Ticket Reconciliation ${jobKey} ${spanLabel(span, 'M.D.YY').replace('–', '-')}.xlsx`);
    };

    // Stamp the whole key (JDE match, the three statuses and their signers)
    // onto the stored rows the grid shows.
    const record = async () => {
        const payload = recordPayload(keyRows);
        if (!payload.length || recording) return;
        setRecording(true);
        try {
            const res = await fieldTicketReconRecord({ job: String(jobKey), by: userName || userEmail || '', rows: payload });
            if (!res) throw new Error('no response from the server');
            if (res.error) throw new Error(res.error);
            showToast(`Recorded the billing key on ${res.updated} row${res.updated === 1 ? '' : 's'}.`, 'success');
            loadStored();
        } catch (e) {
            showToast(`Record failed — ${e?.message || e}`, 'error');
        } finally {
            setRecording(false);
        }
    };

    // ── Stats ──
    const count = (pred) => allRows.filter(pred).length;
    const sumBy = (rows, f) => r2(rows.reduce((a, r) => a + (Number(f(r)) || 0), 0));
    const lastRecorded = useMemo(() => current.reduce((a, r) => (r.recAt && (!a || r.recAt > a.at) ? { at: r.recAt, by: r.recBy } : a), null), [current]);

    const reviewedN = count(reviewed);
    const staleN = count((r) => sides.some((side) => reviewStatus(r, side) === REVIEW_STALE));
    const renderStats = () => {
        const rows = allRows;
        const ticketRows = rows.filter((r) => r.id);
        const orphans = rows.filter((r) => r.jdeStatus === JDE_STATUS.orphan);
        // The HeavyJob side, while it reads or when it can't be read.
        const hjNote = !hj ? null
            : live.loading || (live.key !== rangeKey && !live.error) ? <Text style={muted}>Reading the HeavyJob cards for these dates…</Text>
                : live.error ? <Text style={{ fontSize: 12, color: '#991b1b' }}>{`HeavyJob cards could not be read — ${live.error}. The rows show what was stored; the "now" columns stay blank.`}</Text>
                    : null;
        const hjStats = () => {
            if (!hj || hjNote) return null;
            const changed = count((r) => r.status === HJ_STATUS.changed);
            const removed = count((r) => r.status === HJ_STATUS.removed);
            const added = count((r) => r.status === HJ_STATUS.added || r.status === HJ_STATUS.fresh);
            const moved = changed + removed + added;
            const net = fmtDelta(sumBy(rows, (r) => r.delta));
            return (
                <>
                    <Stat label="Unchanged in HJ" value={count((r) => r.status === HJ_STATUS.same)} tone="#166534" />
                    <Stat
                        label="Changed in HJ"
                        value={moved}
                        sub={[changed ? `${changed} edited` : '', removed ? `${removed} removed` : '', added ? `${added} added` : '', net ? `net ${net} hrs` : ''].filter(Boolean).join(' · ')}
                        tone={moved ? '#991b1b' : undefined}
                    />
                    <Stat label="Ticket ≠ HJ at send" value={count((r) => r.ticketGap)} sub="reviewed card" tone="#475569" />
                </>
            );
        };
        const differs = count((r) => r.jdeStatus === JDE_STATUS.differs || r.jdeStatus === JDE_STATUS.code);
        return (
            <>
                {hjNote ? <div style={{ marginBottom: 8 }}>{hjNote}</div> : null}
                <div className="rc-kpis">
                    <Stat
                        label="Ticket rows"
                        value={ticketRows.length}
                        sub={hj ? `${fmtH(sumBy(ticketRows, (r) => r.hj))} hrs at ticket` : `${fmtH(sumBy(ticketRows, (r) => r.ft))} ticket hrs`}
                    />
                    {hjStats()}
                    <Stat label="Match JDE" value={count((r) => r.jdeStatus === JDE_STATUS.match)} tone="#166534" />
                    <Stat label="JDE ≠ ticket" value={differs} sub={`net ${fmtDelta(sumBy(ticketRows, (r) => r.jdeDelta)) || '0'} hrs`} tone={differs ? '#991b1b' : undefined} />
                    <Stat label="Not in JDE yet" value={count((r) => r.jdeStatus === JDE_STATUS.pending)} tone="#475569" />
                    <Stat
                        label="JDE, no ticket"
                        value={orphans.length}
                        sub={orphans.length ? `${fmtH(sumBy(orphans, (r) => r.jde))} hrs · ${fmtH(sumBy(orphans, (r) => r.jdePd))} per diem` : ''}
                        tone={orphans.length ? '#92400e' : undefined}
                    />
                    <Stat label="Invoice signed" value={count((r) => r.st?.invoice === STATUS.signed)} sub={`${count((r) => r.billedNow === 'Billed' || r.billedNow === 'Partly billed')} on a summary`} />
                    {reviewedN ? <Stat label="Reviewed" value={reviewedN} sub={staleN ? `${staleN} changed since` : ''} tone="#166534" /> : null}
                </div>
            </>
        );
    };

    // The grid fits the card — no sideways scroll: every column takes its
    // share of the width (fixed layout over the colgroup, fitWidths), and a
    // cell too narrow for its text ends in an ellipsis. Day and Type lead, a
    // rule after the last of them.
    const lastFrozen = columns.filter((c) => c.frozen != null).pop()?.key;
    const pin = (c) => (c.key === lastFrozen ? { borderRight: '1px solid #e2e8f0' } : {});
    const colShare = fitWidths(columns, gridW);
    const amtIdx = columns.findIndex((c) => c.key === 'invAmt');
    amtTight.current = amtIdx >= 0 && gridW > 0 && colShare[amtIdx] * gridW < 92;
    const th = (c) => ({
        backgroundColor: '#f8fafc', borderBottom: '1px solid #cbd5e1',
        fontSize: 10, fontWeight: 700, color: '#475569', textAlign: c.right ? 'right' : 'left', verticalAlign: 'bottom',
        padding: `6px ${CELL_PAD / 2}px`, whiteSpace: 'normal', overflow: 'hidden', lineHeight: 1.2,
        ...pin(c),
    });
    const band = (g) => ({
        backgroundColor: g.g ? '#eef2f7' : '#f8fafc', borderBottom: g.g ? '1px solid #cbd5e1' : 'none',
        borderLeft: g.g ? '1px solid #e2e8f0' : 'none', fontSize: 10, fontWeight: 700, letterSpacing: 0.6,
        color: '#64748b', textAlign: 'center', padding: `3px ${CELL_PAD / 2}px`, textTransform: 'uppercase',
        overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
        ...(g.frozen ? { borderRight: '1px solid #e2e8f0' } : {}),
    });
    const cellBase = (c, bg) => ({
        fontSize: 11, color: '#1e293b', textAlign: c.right ? 'right' : 'left',
        paddingTop: 4, paddingBottom: 4, paddingLeft: CELL_PAD / 2, paddingRight: CELL_PAD / 2,
        borderBottom: '1px solid #f1f5f9', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
        fontVariantNumeric: c.right ? 'tabular-nums' : undefined,
        backgroundColor: bg,
        ...pin(c),
    });
    // Grouped, a row's Key indents under its groups: a person's lone row sits
    // level with the person rows beside it, the rows of an open person one
    // step further in.
    const indent = (depth) => (depth === 3 ? 36 : 8 + depth * 8);
    const td = (c, r, depth, off, flag) => ({
        ...cellBase(c, r.id ? undefined : '#fffbeb'),
        ...(grouped && c.key === 'day' ? { paddingLeft: indent(depth) } : {}),
        ...offStyle(off[c.key]?.tone),
        ...(c.key === 'day' ? barStyle(flag) : {}),
    });
    // Group rows: week and day tinted, their label across the leading Day and
    // Type; a person row reads like a row, amber when none of it is ticketed.
    const frozenCols = columns.filter((c) => c.frozen != null);
    const gtd = (c, g) => ({
        ...cellBase(c, g.level === 2 && g.rows.every((r) => !r.id) ? '#fffbeb' : GROUP_BG[g.level]),
        fontWeight: g.level < 2 ? 600 : undefined,
        borderBottom: `1px solid ${g.level === 0 ? '#cbd5e1' : g.level === 1 ? '#e2e8f0' : '#f1f5f9'}`,
    });
    const labelTd = (g) => ({
        ...gtd({ key: lastFrozen, frozen: 0, w: frozenCols.reduce((a, c) => a + c.w, 0) }, g),
        paddingLeft: 8 + g.level * 8,
    });
    const renderGroup = (g) => {
        const open = isOpen(g);
        const chev = <span style={{ display: 'inline-block', width: 12, color: '#64748b', fontWeight: 400 }}>{open ? '▾' : '▸'}</span>;
        const count = plural(g.rows.length, 'row', 'rows');
        // The rows under it that have each column off, and its worst row.
        const offs = g.rows.map(offOf);
        const flag = worst(g.rows.map((r, i) => rowFlag(r, offs[i])));
        const colTd = (c) => {
            const hit = offs.filter((o) => o[c.key]);
            const tone = worst(hit.map((o) => o[c.key].tone));
            return (
                <td key={c.key} title={hit.length ? `${plural(hit.length, 'row differs', 'rows differ')} from the field ticket — open to see` : undefined} style={{ ...gtd(c, g), ...offStyle(tone) }}>
                    {groupCell(c, g.rows, g.level)}
                </td>
            );
        };
        return (
            <tr key={g.key} onClick={() => toggle(g.key)} aria-expanded={open} style={{ cursor: 'pointer' }}>
                {g.level < 2 ? [
                    <td key="label" colSpan={frozenCols.length} style={{ ...labelTd(g), ...barStyle(flag) }}>
                        {canPick ? pickBox(g.level === 0 ? [...new Set(g.rows.map((r) => r.d))] : [g.d], g.level === 0 ? `Pick week ending ${md(g.we)}` : `Pick ${fmtDate(g.d)}`) : null}
                        {chev}
                        {g.level === 0 ? `WE ${md(g.we)}` : fmtDate(g.d)}
                        <span style={{ color: '#64748b', fontWeight: 400, marginLeft: 6 }}>{count}</span>
                    </td>,
                    ...columns.filter((c) => c.frozen == null).map(colTd),
                ] : columns.map((c) => (c.key === 'day'
                    ? <td key={c.key} style={{ ...gtd(c, g), paddingLeft: indent(2), ...barStyle(flag) }}>{chev}{count}</td>
                    : c.key === 'tick' ? <td key={c.key} style={gtd(c, g)}>{tickBox(g.rows, `Tick ${who(g.rows[0]) || count}`)}</td>
                        : colTd(c)))}
            </tr>
        );
    };

    const renderGrid = (empty) => {
        const rows = showAll ? items : items.slice(0, ROW_CAP);
        // No scroll box of its own: the page scrolls, the header held under
        // the filter bar (the scroll listener up top). width 0 / min-width
        // 100% keeps the table from widening the Billing page's content
        // column (a flex item with no min-width).
        return (
            <div ref={gridBoxRef} style={{ width: 0, minWidth: '100%', border: '1px solid #e2e8f0', borderRadius: 4 }}>
                <table style={{ borderCollapse: 'separate', borderSpacing: 0, width: '100%', tableLayout: 'fixed' }}>
                    <colgroup>
                        {columns.map((c, i) => <col key={c.key} style={{ width: `${(colShare[i] * 100).toFixed(3)}%` }} />)}
                    </colgroup>
                    {/* Positioned above the body's cells (RN-web Views sit at
                        z 0 and come later in the DOM) as it slides over them. */}
                    <thead ref={headRef} style={{ position: 'relative', zIndex: 4 }}>
                        <tr>
                            {groupSpans(columns).map((g) => <th key={`g-${g.key}`} colSpan={g.n} style={band(g)}>{g.g}</th>)}
                        </tr>
                        <tr>
                            {columns.map((c) => (
                                <th key={c.key} title={c.key === 'tick' ? 'Tick every row shown' : c.note || ''} onClick={c.onHead} style={{ ...th(c), cursor: c.onHead ? 'pointer' : undefined, userSelect: c.onHead ? 'none' : undefined }}>
                                    {c.key === 'tick' ? tickBox(shown, 'Tick every row shown') : c.head ? c.head() : c.label}
                                </th>
                            ))}
                        </tr>
                    </thead>
                    <tbody>
                        {rows.map(({ g, r, depth }, i) => {
                            if (g) return renderGroup(g);
                            const off = offOf(r);
                            const flag = rowFlag(r, off);
                            return (
                                <tr key={r.id || `x${i}|${r.d}|${r.k}|${who(r)}|${r.cc}`}>
                                    {columns.map((c) => (
                                        <td key={c.key} title={off[c.key]?.tip} style={td(c, r, depth, off, flag)}>
                                            {canPick && !grouped && c.key === 'day' ? pickBox([r.d], `Pick ${fmtDate(r.d)}`) : null}
                                            {c.cell ? c.cell(r) : c.get(r)}
                                        </td>
                                    ))}
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
                {!rows.length ? (
                    <div style={{ fontSize: 12, color: empty?.error ? '#991b1b' : '#64748b', padding: '14px 12px' }}>
                        {empty?.text || (issuesOnly ? 'Nothing to flag — every row reconciles.' : 'No rows match the filters.')}
                    </div>
                ) : null}
                {items.length > rows.length ? (
                    <div style={{ width: 'fit-content' }}>
                        <Pressable onPress={() => setShowAll(true)} style={{ paddingTop: 8, paddingBottom: 8, paddingLeft: 12 }}>
                            <Text style={{ fontSize: 11, color: '#1e40af', fontWeight: '600' }}>{`Show all ${items.length} lines`}</Text>
                        </Pressable>
                    </div>
                ) : null}
            </div>
        );
    };

    // ── Render ──
    if (!jobKey) return <View style={card}><Text style={muted}>Pick a job.</Text></View>;

    const noSnaps = !snaps.loading && !snaps.error && !snaps.list.length;
    // What the grid says under its headers when it has no rows.
    const empty = invoice ? (
        stored.error ? { error: true, text: `Could not read the field tickets for these dates — ${stored.error}` }
            : costLoading ? { text: 'Cost history still loading…' }
                : stored.loading && !current.length ? { text: 'Loading the field tickets for these dates…' }
                    : null
    ) : snaps.loading && !snaps.list.length ? { text: 'Loading the reconciliation table…' }
        : snaps.error ? { error: true, text: `Could not read the reconciliation table — ${snaps.error}` }
            : noSnaps ? {
                text: 'No field ticket snapshots on this job yet. Every ticket sent from Daily Field Tickets › Ticket Map records one; '
                    + 'a ticket that went out before this existed (or on paper) can be recorded with Record HJ snapshot on its send.',
            }
                : stored.error ? { error: true, text: `Could not read these dates — ${stored.error}` }
                    : stored.loading && !current.length ? { text: 'Loading the dates…' }
                        : null;
    // The date picker: every week a ticket went out with its sends, then the
    // ranges. A week with no sends gets its own line; any other span is Custom.
    const spanWe = start && sameSpan(span, weekOf(weekEnding(start))) ? weekEnding(start) : '';
    const preset = DATE_PRESETS.find((p) => sameSpan(p.span(), span));
    const dateValue = spanWe ? `w:${spanWe}` : preset ? `p:${preset.id}` : allSpan && sameSpan(allSpan, span) ? 'all' : 'custom';
    const weekOpts = spanWe && !weeks.some((w) => w.we === spanWe)
        ? [...weeks, { we: spanWe, snaps: [] }].sort((a, b) => b.we.localeCompare(a.we))
        : weeks;
    const pickDate = (v) => {
        if (v === 'all') pickSpan(allSpan);
        else if (v.startsWith('w:')) pickSpan(weekOf(v.slice(2)));
        else if (v.startsWith('p:')) pickSpan(DATE_PRESETS.find((p) => p.id === v.slice(2)).span());
    };
    // ‹ › step a week either side of the span's last week.
    const endWe = weekEnding(end || iso(moment()));
    const stepWeek = (n) => pickSpan(weekOf(iso(moment(endWe, 'YYYY-MM-DD').add(7 * n, 'days'))));

    // The live state worth a glance, by the buttons it belongs to.
    const notes = (invoice
        ? [costLoading ? 'Cost history still loading — JDE columns fill in when it lands.' : '', onClass ? "Click a labor row's billing class to adjust it." : '']
        : [
            live.at && live.key === rangeKey ? `HeavyJob read ${moment(live.at).format('h:mm a')}` : '',
            current.some((r) => r.src === 'heet') ? 'Some days went out on HEET, not the live cards — their "at ticket" hours are HEET\'s.' : '',
            costLoading ? 'Cost history still loading — JDE columns fill in when it lands.' : '',
            lastRecorded ? `Last recorded ${fmtUtc(lastRecorded.at)}${lastRecorded.by ? ` by ${lastRecorded.by}` : ''}` : 'Not recorded yet',
        ]).filter(Boolean).join(' · ');
    // The sends the dates cover: one tag each for a week, a count for more.
    const sends = !end ? null : multiWeek || !spanSnaps.length ? (
        <span className="rc-muted">
            {[`${md(start)}–${md(end)}`, plural(spanWeeks, 'week', 'weeks'), spanSnaps.length ? plural(spanSnaps.length, 'send', 'sends') : 'no field tickets sent'].join(' · ')}
        </span>
    ) : (
        <div className="rc-sends">
            {spanSnaps.map((s) => (
                <span
                    key={s.Snapshot_ID}
                    className="rc-send"
                    title={[
                        s.Doc_ID ? `ticket ${s.Doc_ID}` : '',
                        s.Capture_Source === 'manual' ? 'recorded by hand'
                            : s.Capture_Source === 'backfill' ? 'rebuilt from the sent PDF'
                            : s.Capture_Source === 'auto' ? 'recorded by the nightly sweep (HeavyJob as of then)'
                                : s.Capture_Source === 'draft' ? 'draft envelope' : 'sent',
                        s.Captured_By || '',
                        fmtUtc(s.Captured_At_Utc),
                    ].filter(Boolean).join(' · ')}
                >
                    {[`${md(s.First_Date)}${s.Last_Date !== s.First_Date ? `–${md(s.Last_Date)}` : ''}`, PART_LABEL[s.Part] || s.Part || '', `${s.Rows} rows`].filter(Boolean).join(' · ')}
                </span>
            ))}
        </div>
    );
    const levels = [{ value: 0, label: 'Weeks' }, { value: 1, label: 'Days' }, { value: 2, label: groupBy === 'jde' ? 'JDE keys' : 'People' }, { value: 3, label: 'Rows' }];
    const exportBtn = <Btn label="Export to Excel" onPress={exportXlsx} disabled={!shown.length} />;

    return (
        <View style={invoice ? { marginBottom: 4 } : card}>
            <style>{CSS}</style>
            {invoice ? null : (
                <div className="rc-row" style={{ marginBottom: 12 }}>
                    <span className="rc-title">Field Ticket Reconciliation</span>
                    <span className="rc-gap" />
                    {notes ? <span className="rc-muted" style={{ marginRight: 4 }}>{notes}</span> : null}
                    <Btn label={live.loading ? 'Reading HeavyJob…' : '↻ Refresh HeavyJob'} onPress={loadLive} disabled={live.loading || !end} />
                    <Btn label={recording ? 'Recording…' : 'Record to table'} onPress={record} disabled={recording || costLoading || !current.length} />
                    {exportBtn}
                </div>
            )}

            {!invoice && (weeks.length || end) ? (
                <div className="rc-row" style={{ marginBottom: 14 }}>
                    <Btn kind="step" label="‹" aria="Week before" title="Week before" onPress={() => stepWeek(-1)} />
                    <select className="rc-field" aria-label="Dates" value={dateValue} onChange={(e) => pickDate(e.target.value)} style={{ minWidth: 150 }}>
                        {weekOpts.length ? (
                            <optgroup label="Week ending">
                                {weekOpts.map((w) => (
                                    <option key={w.we} value={`w:${w.we}`}>{`WE ${md(w.we)} · ${w.snaps.length ? plural(w.snaps.length, 'send', 'sends') : 'no sends'}`}</option>
                                ))}
                            </optgroup>
                        ) : null}
                        <optgroup label="Range">
                            {DATE_PRESETS.map((p) => <option key={p.id} value={`p:${p.id}`}>{p.label}</option>)}
                            {allSpan ? <option value="all">All dates</option> : null}
                            {dateValue === 'custom' ? <option value="custom" disabled>Custom range</option> : null}
                        </optgroup>
                    </select>
                    <Btn kind="step" label="›" aria="Week after" title="Week after" onPress={() => stepWeek(1)} disabled={endWe >= thisWe()} />
                    <input type="date" className="rc-field" aria-label="From" value={start || ''} onChange={(e) => setFrom(e.target.value || '')} />
                    <span className="rc-muted">–</span>
                    <input type="date" className="rc-field" aria-label="To" value={end || ''} onChange={(e) => setTo(e.target.value || '')} />
                    <span className="rc-lbl">Compare</span>
                    <Seg
                        label="Compare"
                        options={[{ value: 'latest', label: 'Latest send' }, { value: 'first', label: 'First send' }]}
                        value={mode}
                        onPick={setMode}
                    />
                    <span className="rc-gap" />
                    {sends}
                </div>
            ) : null}

            {end ? <div style={{ marginBottom: 14 }}>{renderStats()}</div> : null}

            {/* Held at the top of the page while the grid scrolls under it. */}
            <div ref={barRef} style={{ position: 'relative', zIndex: 5, backgroundColor: '#ffffff', paddingTop: 8, marginTop: -8, paddingBottom: 10 }}>
            <div className="rc-row">
                <Seg label="Type" options={['All', 'Labor', 'Equipment'].map((k) => ({ value: k, label: k }))} value={kind} onPick={setKind} />
                <Toggle label="Exceptions only" on={issuesOnly} onPress={() => setIssuesOnly((v) => !v)} />
                {billedCount ? <Toggle label={`Show billed (${billedCount})`} on={showBilled} onPress={() => setShowBilled((v) => !v)} /> : null}
                <input
                    type="search"
                    className="rc-field"
                    aria-label="Search"
                    value={q}
                    onChange={(e) => setQ(e.target.value)}
                    placeholder="Search name, ID, cost code…"
                    style={{ width: 220 }}
                />
                <span className="rc-gap" />
                <span className="rc-lbl">Group</span>
                <select className="rc-field" aria-label="Group by" value={groupBy} onChange={(e) => { setGroupBy(e.target.value); setShowAll(false); }}>
                    {GROUP_BY.map((o) => <option key={o.id} value={o.id}>{o.label}</option>)}
                </select>
                {grouped ? (
                    <>
                        <span className="rc-lbl">Open to</span>
                        <Seg label="Open to" options={levels} value={baseLevel} onPick={(v) => { setExpand({ level: v, flip: {} }); setShowAll(false); }} />
                    </>
                ) : null}
                {invoice ? exportBtn : null}
            </div>
            {invoice && notes ? <div className="rc-muted" style={{ marginTop: 10 }}>{notes}</div> : null}

            {canPick && pickDates.length ? (
                <div className="rc-row rc-pick" style={{ marginTop: 10 }}>
                    <span style={{ fontSize: 12, fontWeight: 700, color: '#0f172a' }}>{`${plural(pickDates.length, 'day', 'days')} picked`}</span>
                    <span className="rc-muted" style={{ marginRight: 6 }}>
                        {`${pickDates.slice(0, 8).map(fmtDate).join(', ')}${pickDates.length > 8 ? ` +${pickDates.length - 8}` : ''}`}
                    </span>
                    {onCreateTicket ? CREATE.map((o) => <Btn key={o.kind} label={o.label} onPress={() => createTicket(o.kind)} disabled={!o.ok(pickDates)} />) : null}
                    {onCreateInvoice ? (
                        <Btn
                            label={`Create invoice · ${plural(pickWeeks.length, 'week', 'weeks')}`}
                            title={`Invoice Workbook builds the invoice folder for ${pickWeeks.map((we) => `WE ${md(we)}`).join(', ')}`}
                            onPress={() => onCreateInvoice(pickWeeks)}
                        />
                    ) : null}
                    <Btn kind="link" label="Clear" onPress={() => setPickedDays({})} />
                    <div className="rc-muted" style={{ width: '100%' }}>
                        {[
                            onCreateTicket ? 'A ticket opens Daily Field Tickets › Ticket Map with a Draft on these days to review — nothing goes to DocuSign until you send it there.' : '',
                            ...(onCreateTicket ? CREATE.filter((o) => !o.ok(pickDates)).map((o) => `${o.label}: ${o.why}.`) : []),
                            onCreateInvoice ? `Create invoice opens Invoice Workbook with ${pickWeeks.length === 1 ? 'the week' : 'these weeks'} picked and builds the invoice folder, recorded there as a draft for someone else to validate; a partly picked week bills whole.` : '',
                        ].filter(Boolean).join(' ')}
                    </div>
                    {pickDrafts.length ? (
                        <div className="rc-muted" style={{ width: '100%', color: '#92400e' }}>
                            {`Already drafted — ${pickDrafts.slice(0, 3).map(draftLine).join('; ')}${pickDrafts.length > 3 ? `; +${pickDrafts.length - 3} more` : ''}.`}
                        </div>
                    ) : null}
                </div>
            ) : null}
            {tickKeys.length ? (
                <div className="rc-row rc-pick" style={{ marginTop: 10 }}>
                    <span style={{ fontSize: 12, fontWeight: 700, color: '#0f172a' }}>{`${plural(tickKeys.length, 'row', 'rows')} ticked`}</span>
                    <input
                        type="text"
                        className="rc-field"
                        aria-label="Why it differs"
                        value={note}
                        onChange={(e) => setNote(e.target.value)}
                        onKeyDown={(e) => { if (e.key === 'Enter') markReviewed(); }}
                        placeholder="Why it differs — e.g. client approved the extra hours by email"
                        maxLength={1000}
                        style={{ flex: 1, minWidth: 260 }}
                    />
                    <Btn label={reviewing ? 'Saving…' : 'Mark reviewed'} onPress={markReviewed} disabled={reviewing || !note.trim()} title={note.trim() ? '' : 'Write why the rows differ first'} />
                    <Btn kind="link" label="Clear" onPress={() => setTicked({})} />
                    <div className="rc-muted" style={{ width: '100%' }}>
                        Reviewed rows leave Exceptions only on both tabs and keep the note on their check pill; they flag again if the hours move.
                    </div>
                </div>
            ) : null}
            </div>
            {renderGrid(empty)}
        </View>
    );
};

export default BillingReconciliation;
